import { createContext, useContext, useState, ReactNode } from "react";

export type Language = "uz" | "ru" | "en";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations: Record<Language, Record<string, string>> = {
  en: {
    // Common
    "common.start": "Get Started",
    "common.submitRequest": "Submit Request",

    // Header
    "nav.marketing": "Marketing",
    "nav.solutions": "Solutions",
    "nav.contact": "Contact",
    "nav.callback": "Request a Call",
    "nav.smm": "SMM",
    "nav.smm.desc": "Social Media Marketing",
    "nav.research": "Market Research",
    "nav.research.desc": "Data-driven insights",
    "nav.crm": "CRM Implementation",
    "nav.crm.desc": "Customer relationship systems",
    "nav.automation": "Process Automation",
    "nav.automation.desc": "Streamline workflows",
    "nav.bi": "BI Dashboards",
    "nav.bi.desc": "Business intelligence",
    "nav.custom": "Custom Solutions",
    "nav.custom.desc": "Tailored development",

    // Callback Modal
    "callback.title": "Request a Call",
    "callback.description": "Leave your details and we will call you back within 15 minutes",
    "callback.name": "Name",
    "callback.namePlaceholder": "Your name",
    "callback.phone": "Phone",
    "callback.sending": "Sending...",
    "callback.success": "Request sent!",
    "callback.successDesc": "We will call you back within 15 minutes",
    "callback.error": "Error",
    "callback.fillFields": "Please fill in all fields correctly",

    // Hero
    "hero.badge": "Digital Business Partner",
    "hero.title1": "Binar+",
    "hero.title2": "Your Trusted Partner for Digital Business Transformation",
    "hero.description":
      "We partner with businesses to implement strategic marketing and technology solutions that drive measurable growth.",
    "hero.feature1": "CRM & ERP Integration",
    "hero.feature2": "Process Automation",
    "hero.feature3": "BI Analytics",
    "hero.cta": "Get Started",
    "hero.ctaSecondary": "Explore Solutions",

    // Sections
    "marketing.label": "Marketing",
    "marketing.title": "Strategic Marketing Services",
    "marketing.description":
      "Reach your target audience and build lasting brand awareness with our comprehensive marketing solutions.",

    // SMM Homepage Section
    "smm.label": "Marketing",
    "smm.title": "SMM Marketing",
    "smm.description":
      "We help businesses grow their presence on social media. Our team creates engaging content, runs advertising campaigns, and builds active communities on Instagram, Facebook, TikTok and other platforms.",
    "smm.home.feature1": "Content strategy development",
    "smm.home.feature2": "Targeted advertising campaigns",
    "smm.home.feature3": "Community management",
    "smm.home.feature4": "Analytics and reporting",
    "smm.cta": "Learn More",

    // Market Research Homepage Section
    "research.label": "Marketing",
    "research.title": "Market Research",
    "research.description":
      "We conduct comprehensive market research to help you understand your audience, competitors, and industry trends. Our data-driven insights enable smarter business decisions.",
    "research.home.feature1": "Consumer behavior analysis",
    "research.home.feature2": "Competitive intelligence",
    "research.home.feature3": "Market trends forecasting",
    "research.home.feature4": "Data-driven recommendations",
    "research.cta": "Learn More",

    // Budget Request Section
    "budget.promo.banner": "SMM Audit FREE until 01.03.2026!",
    "budget.label": "Marketing Solutions",
    "budget.title": "Order SMM Audit & Strategy",
    "budget.description":
      "Get a personalized marketing budget estimate and discover how our integrated technology stack can transform your business operations.",
    "budget.feature1": "Full integration with CRM, ERP, and BI systems",
    "budget.feature2": "Automated marketing workflows",
    "budget.feature3": "Real-time analytics and reporting",
    "budget.form.title": "Get Your Estimate",
    "budget.form.name": "Name",
    "budget.form.namePlaceholder": "Your name",
    "budget.form.phone": "Phone",
    "budget.form.email": "Email",
    "budget.form.calculate": "Order",
    "budget.form.sending": "Sending...",
    "budget.form.success": "Request sent!",
    "budget.form.successDescription": "Our specialist will contact you within 24 hours.",
    "budget.form.error": "Error",
    "budget.form.errorDescription": "Please fill in the required fields.",

    // Systems Implementation Section
    "systems.label": "Solutions",
    "systems.title": "CRM, BPM, ERP Implementation",
    "systems.description":
      "We implement and customize enterprise management systems that optimize your business processes, increase efficiency, and provide complete control over company operations.",
    "systems.home.feature1": "Selection and implementation of a system for your business",
    "systems.home.feature2": "System configuration for your business tasks",
    "systems.home.feature3": "Integration with necessary systems",
    "systems.home.feature4": "Staff training on system operation",
    "systems.cta": "Learn More",

    // Automation Homepage Section
    "automation.label": "Solutions",
    "automation.title": "Business Process Automation",
    "automation.description":
      "We implement cutting-edge automation solutions using robotic process automation (RPA), AI agents, machine learning, and intelligent workflows to transform your business operations.",
    "automation.home.feature1": "Robotic Process Automation (RPA)",
    "automation.home.feature2": "AI agents and virtual assistants",
    "automation.home.feature3": "Intelligent document processing",
    "automation.home.feature4": "No-code/low-code automation platforms",
    "automation.cta": "Learn More",

    // BI Homepage Section
    "bi.label": "Solutions",
    "bi.title": "Business Analytics & BI Dashboards",
    "bi.description":
      "We create interactive dashboards and analytical solutions that transform your data into clear insights for strategic decision-making.",
    "bi.home.feature1": "Interactive Power BI dashboards",
    "bi.home.feature2": "Real-time data visualization",
    "bi.home.feature3": "KPI monitoring and reporting",
    "bi.home.feature4": "Data integration from multiple sources",
    "bi.cta": "Learn More",

    // Custom Solutions Homepage Section
    "custom.label": "Solutions",
    "custom.home.title": "Custom Software Solutions",
    "custom.home.description":
      "We develop custom software tailored to your unique business needs — from websites and mobile applications to decision-making systems (scoring) and comprehensive enterprise solutions.",
    "custom.home.feature1": "Corporate websites and web applications",
    "custom.home.feature2": "Mobile apps for iOS and Android",
    "custom.home.feature3": "Scoring and decision-making systems",
    "custom.home.feature4": "Integration and custom API development",
    "custom.cta": "Learn More",

    // About Company Section
    "about.label": "About Us",
    "about.title": "Why Choose Binar+",
    "about.description":
      "We are part of a fintech group with extensive experience in building high-load systems for businesses in the EU and CIS markets.",
    "about.advantage1": "Years in Business",
    "about.advantage2": "EU & CIS Markets Experience",
    "about.advantage3": "Part of Fintech Group",
    "about.advantage4": "High-Load Projects",

    // Principles Section
    "principles.label": "Our Principles",
    "principles.title": "How We Work",
    "principles.principle1.title": "We Always Deliver",
    "principles.principle1.description":
      "We see every project through to completion. No matter the challenges, we find solutions and deliver results.",
    "principles.principle2.title": "We Take Responsibility",
    "principles.principle2.description":
      "We stand behind our work and are accountable for the outcomes. Your success is our measure of success.",
    "principles.principle3.title": "Business-First Approach",
    "principles.principle3.description":
      "We help build business systems, considering the specifics of the client's business with mandatory attention to legal requirements and security standards.",

    // Testimonials Section
    "testimonials.label": "Testimonials",
    "testimonials.title": "What Our Clients Say",
    "testimonials.description": "We are proud to work with leading companies in the EU, CIS, and Central Asia markets.",
    "testimonials.country.belarus": "Belarus",
    "testimonials.country.russia": "Russia",
    "testimonials.country.kazakhstan": "Kazakhstan",
    "testimonials.review1":
      "Binar+ implemented a comprehensive CRM system that increased our sales team efficiency by 40%. Their expertise in fintech is unmatched.",
    "testimonials.review2":
      "The automation solutions provided by Binar+ helped us reduce manual processing time by 60%. Highly professional team.",
    "testimonials.review3":
      "Outstanding BI dashboards that give us real-time insights into our leasing portfolio. A game-changer for our decision-making.",
    "testimonials.review4":
      "Working with Binar+ on our digital transformation was seamless. They understand fintech regulations and security requirements.",
    "testimonials.review5":
      "The custom scoring system developed by Binar+ improved our credit decisions accuracy significantly. Excellent technical expertise.",
    "testimonials.review6":
      "Their process automation solutions helped us scale our operations without proportionally increasing headcount.",
    "testimonials.review7":
      "Binar+ delivered a complete ERP integration that unified all our business processes. Professional and reliable partner.",
    "testimonials.review8":
      "The marketing analytics platform they built gives us unprecedented visibility into our customer acquisition funnel.",
    "testimonials.review9":
      "From consultation to implementation, Binar+ demonstrated deep knowledge of both technology and business needs.",
    "testimonials.author1": "Alexander Petrov",
    "testimonials.position1": "CTO",
    "testimonials.author2": "Maria Ivanova",
    "testimonials.position2": "Operations Director",
    "testimonials.author3": "Dmitry Kozlov",
    "testimonials.position3": "CEO",
    "testimonials.author4": "Elena Smirnova",
    "testimonials.position4": "Head of Digital",
    "testimonials.author5": "Sergey Volkov",
    "testimonials.position5": "IT Director",
    "testimonials.author6": "Anna Kuznetsova",
    "testimonials.position6": "COO",
    "testimonials.author7": "Nursultan Akhmetov",
    "testimonials.position7": "Managing Director",
    "testimonials.author8": "Aida Nurbekova",
    "testimonials.position8": "Marketing Director",
    "testimonials.author9": "Bekzat Yessenov",
    "testimonials.position9": "CEO",

    "solutions.label": "Solutions",
    "solutions.title": "Technology Solutions",
    "solutions.description":
      "Leverage cutting-edge technology to optimize operations, improve efficiency, and accelerate business growth.",

    // Services
    "service.smm.title": "Social Media Marketing",
    "service.smm.description":
      "Build your brand presence and engage your audience across all major social platforms with data-driven strategies.",
    "service.research.title": "Market Research",
    "service.research.description":
      "Gain deep insights into your market, competitors, and customers to make informed business decisions.",
    "service.crm.title": "CRM Implementation",
    "service.crm.description":
      "Implement and customize CRM systems that streamline customer relationships and boost sales efficiency.",
    "service.automation.title": "Process Automation",
    "service.automation.description":
      "Automate repetitive tasks and workflows to increase productivity and reduce operational costs.",
    "service.bi.title": "BI Dashboards",
    "service.bi.description":
      "Transform your data into actionable insights with custom business intelligence dashboards.",
    "service.custom.title": "Custom Solutions",
    "service.custom.description":
      "Get tailored software solutions designed specifically for your unique business challenges.",
    "service.learnMore": "Learn more",

    // CTA Banner
    "cta.title": "Ready to transform your business?",
    "cta.description": "Let's discuss how our solutions can help you achieve your goals.",
    "cta.button": "Get in Touch",

    // Footer
    "footer.about":
      "Empowering businesses through digital transformation. We combine strategic thinking with technical expertise to deliver solutions that drive growth.",
    "footer.privacy": "Privacy Policy",
    "footer.terms": "Terms of Service",
    "footer.rights": "All rights reserved.",
    "footer.address.line1": "Republic of Uzbekistan, 100170,",
    "footer.address.line2": "Tashkent city, Mirzo Ulugbek District,",
    "footer.address.line3": "Ziyolilar Street, 26, 1st floor (lounge hall)",
    "cookie.title": "We use cookies",
    "cookie.description":
      "This website uses cookies to improve your experience and analyze site traffic. By continuing to use the site, you agree to our use of cookies.",
    "cookie.accept": "Accept",
    "cookie.decline": "Decline",
    "footer.form.title": "Ready to transform your business?",
    "footer.form.description": "Let's discuss how our solutions can help you achieve your goals.",
    "footer.form.company": "Company Name",
    "footer.form.companyPlaceholder": "Your company name",
    "footer.form.message": "Message",
    "footer.form.messagePlaceholder": "Tell us about your project...",
    "footer.form.submit": "Send Request",

    // Contact Page
    "contact.title": "Let's Work Together",
    "contact.subtitle":
      "Ready to transform your business? Get in touch and let's discuss how we can help you achieve your goals.",
    "contact.form.title": "Send Us a Message",
    "contact.form.description": "Fill out the form below and we'll get back to you within one business day.",
    "contact.form.name": "Name",
    "contact.form.name.placeholder": "Your name",
    "contact.form.email": "Email",
    "contact.form.email.placeholder": "your@email.com",
    "contact.form.company": "Company",
    "contact.form.company.placeholder": "Your company",
    "contact.form.service": "Service Interest",
    "contact.form.service.placeholder": "Select a service",
    "contact.form.message": "Message",
    "contact.form.message.placeholder": "Tell us about your project or ask us a question...",
    "contact.form.submit": "Send Message",
    "contact.form.sending": "Sending...",
    "contact.form.success.title": "Message sent!",
    "contact.form.success.description": "We'll get back to you as soon as possible.",
    "contact.info.title": "Contact Information",
    "contact.info.email": "Email",
    "contact.info.phone": "Phone",
    "contact.info.office": "Office",
    "contact.social.title": "Follow Us",
    "contact.map.title": "Find Us",

    // Services dropdown
    "service.smm.label": "Social Media Marketing",
    "service.research.label": "Market Research",
    "service.crm.label": "CRM Implementation",
    "service.automation.label": "Process Automation",
    "service.bi.label": "BI Dashboards",
    "service.custom.label": "Custom Solutions",
    "service.other.label": "Other",

    // SMM Page
    "smm.hero.label": "Marketing",
    "smm.hero.title": "Social Media Marketing",
    "smm.hero.description":
      "Build meaningful connections with your audience and grow your brand presence across all major social platforms.",
    "smm.overview.title": "Engage Your Audience Where They Are",
    "smm.overview.description":
      "SMM marketing in Uzbekistan is one of the most effective ways to promote business online. We work not for likes, but for real leads, clients, and sales.",
    "smm.overview.benefit1": "Audit and target audience evaluation",
    "smm.overview.benefit2": "SMM strategy development for your business",
    "smm.overview.benefit3": "Account packaging and design",
    "smm.overview.benefit4": "Creating selling and engaging content",
    "smm.overview.benefit5": "Launch and optimization of targeted advertising",
    "smm.overview.benefit6": "Analytics, testing and scaling results",
    "smm.overview.cta": "Submit Application",
    "smm.process.title": "Work Stages",
    "smm.process.description": "A proven approach to building and executing successful social media strategies.",
    "smm.process.step1.title": "Business Analysis",
    "smm.process.step1.description": "We study the niche, target audience, competitors, and current state of accounts.",
    "smm.process.step2.title": "Promotion Strategy",
    "smm.process.step2.description": "We define goals, content format, sales funnel, and advertising model.",
    "smm.process.step3.title": "Account Packaging",
    "smm.process.step3.description": "We design the profile to build trust and motivate people to contact you.",
    "smm.process.step4.title": "Content & Advertising",
    "smm.process.step4.description":
      "We regularly publish content + launch targeted advertising with hypothesis testing.",
    "smm.process.step5.title": "Analytics & Growth",
    "smm.process.step5.description": "We track metrics, strengthen working connections, and increase results.",
    "smm.features.title": "The End Result",
    "smm.features.description": "Social media starts working for your business, not just existing.",
    "smm.feature1": "Growth in reach and brand awareness",
    "smm.feature2": "Stable flow of leads from social media",
    "smm.feature3": "Engaged and interested audience",
    "smm.feature4": "Reduced customer acquisition cost (CAC)",
    "smm.feature5": "Clear and manageable customer acquisition system",
    "smm.cta.title": "Ready to grow your social presence?",
    "smm.cta.description": "Let's create a social media strategy that connects with your audience and drives results.",
    "smm.platform.linkedin": "LinkedIn",
    "smm.platform.linkedin.desc": "B2B networking and thought leadership",
    "smm.platform.instagram": "Instagram",
    "smm.platform.instagram.desc": "Visual storytelling and brand awareness",
    "smm.platform.facebook": "Facebook",
    "smm.platform.facebook.desc": "Community building and advertising",
    "smm.platform.twitter": "Twitter/X",
    "smm.platform.twitter.desc": "Real-time engagement and news",

    // Research Page
    "research.hero.label": "Marketing",
    "research.hero.title": "Market Research",
    "research.hero.description":
      "Make confident business decisions backed by comprehensive market intelligence and customer insights.",
    "research.overview.title": "Insights That Drive Growth",
    "research.overview.description":
      "Understanding your market is the foundation of business success. Our research services combine rigorous methodology with practical business acumen to deliver insights you can act on.",
    "research.overview.text":
      "Whether you're entering a new market, launching a product, or seeking to better understand your customers, we provide the data and analysis you need to move forward with confidence.",
    "research.overview.benefit1": "Primary and secondary research capabilities",
    "research.overview.benefit2": "Quantitative and qualitative methodologies",
    "research.overview.benefit3": "Industry-specific expertise",
    "research.overview.benefit4": "Actionable strategic recommendations",
    "research.decisions.title": "How Research Drives Decisions",
    "research.decisions.step1.title": "Identify the Question",
    "research.decisions.step1.desc": "What decision needs to be made?",
    "research.decisions.step2.title": "Gather Evidence",
    "research.decisions.step2.desc": "What data supports each option?",
    "research.decisions.step3.title": "Analyze & Recommend",
    "research.decisions.step3.desc": "What does the evidence suggest?",
    "research.decisions.step4.title": "Act with Confidence",
    "research.decisions.step4.desc": "Move forward with reduced risk.",
    "research.types.title": "Research Services",
    "research.types.description": "Comprehensive research solutions tailored to your business needs.",
    "research.type.customer.title": "Customer Research",
    "research.type.customer.description":
      "Understand your customers' needs, preferences, and pain points through surveys, interviews, and behavioral analysis.",
    "research.type.competitor.title": "Competitor Analysis",
    "research.type.competitor.description":
      "Map your competitive landscape, identify market gaps, and benchmark your performance against industry leaders.",
    "research.type.sizing.title": "Market Sizing",
    "research.type.sizing.description":
      "Quantify market opportunities, assess market potential, and identify growth segments for strategic planning.",
    "research.type.strategy.title": "Strategy Development",
    "research.type.strategy.description":
      "Develop comprehensive strategies for product, marketing, personnel, finance and investments based on market data.",
    "research.process.title": "Our Research Process",
    "research.process.description": "A systematic approach to uncovering actionable market insights.",
    "research.process.step1.title": "Define Objectives",
    "research.process.step1.description": "We work with you to identify key questions and research goals.",
    "research.process.step2.title": "Data Collection",
    "research.process.step2.description": "Gathering qualitative and quantitative data through proven methodologies.",
    "research.process.step3.title": "Analysis",
    "research.process.step3.description": "Our analysts extract meaningful patterns and actionable insights.",
    "research.process.step4.title": "Recommendations",
    "research.process.step4.description": "Clear, strategic recommendations to guide your business decisions.",
    "research.benefits.title": "Benefits of Market Research",
    "research.benefits.description": "Invest in understanding your market to gain a competitive edge.",
    "research.benefit1": "Reduce risk in strategic decision-making",
    "research.benefit2": "Identify new market opportunities",
    "research.benefit3": "Understand customer needs and expectations",
    "research.benefit4": "Benchmark against competitors",
    "research.benefit5": "Validate ideas, products and services before launch",
    "research.benefit6": "Optimize pricing strategies",
    "research.benefit7": "Improve customer satisfaction and loyalty",
    "research.benefit8": "Support investment and growth planning",
    "research.cta.title": "Need insights to guide your strategy?",
    "research.cta.description": "Let's discuss how market research can inform your business decisions.",

    // CRM Page
    "crm.hero.label": "Solutions",
    "crm.hero.title": "CRM Implementation",
    "crm.hero.description": "Transform your customer relationships with a CRM system tailored to your business needs.",
    "crm.overview.title": "Build Stronger Customer Relationships",
    "crm.overview.description":
      "A well-implemented CRM is the backbone of successful customer management. We help you select, configure, and deploy CRM solutions that drive sales efficiency and customer satisfaction.",
    "crm.benefit1": "360-degree view of customer relationships",
    "crm.benefit2": "Improved sales team productivity",
    "crm.benefit3": "Better forecasting accuracy",
    "crm.benefit4": "Enhanced customer service",
    "crm.benefit5": "Streamlined communication",
    "crm.benefit6": "Data-driven decision making",
    "crm.why.title": "Why Professional Implementation Matters",
    "crm.why.text1":
      "Many CRM implementations fail not because of the technology, but due to poor planning, inadequate customization, or lack of user adoption.",
    "crm.why.text2":
      "Our approach ensures your CRM is configured to match how your team actually works, with clean data migration and comprehensive training that drives adoption from day one.",
    "crm.why.result": "The result: a CRM that your team actually uses and that delivers real business value.",
    "crm.services.title": "Our CRM Services",
    "crm.services.description": "End-to-end CRM implementation support for your business.",
    "crm.service.selection.title": "System Selection",
    "crm.service.selection.description":
      "We help you choose the right CRM platform based on your industry, size, and specific requirements.",
    "crm.service.config.title": "Custom Configuration",
    "crm.service.config.description":
      "Tailored setup including custom fields, workflows, automations, and integrations.",
    "crm.service.integration.title": "Integration",
    "crm.service.integration.description":
      "Connect your CRM with email, marketing tools, accounting software, and other business systems.",
    "crm.service.support.title": "Ongoing Support",
    "crm.service.support.description":
      "Continuous optimization, troubleshooting, and updates as your business evolves.",
    "crm.process.title": "Implementation Process",
    "crm.process.description": "A structured approach to successful CRM deployment.",
    "crm.process.step1.title": "Assessment",
    "crm.process.step1.description":
      "We analyze your current processes and identify the ideal CRM solution for your needs.",
    "crm.process.step2.title": "Configuration",
    "crm.process.step2.description": "Customizing the CRM to match your workflows, data structure, and business rules.",
    "crm.process.step3.title": "Migration",
    "crm.process.step3.description": "Securely transferring existing customer data with validation and cleanup.",
    "crm.process.step4.title": "Training",
    "crm.process.step4.description": "Comprehensive training to ensure your team maximizes the CRM's potential.",
    "crm.results.title": "Results After Implementation",
    "crm.results.description": "The company receives not just a CRM, but a centralized tool for managing results.",
    "crm.result1": "Unified client database",
    "crm.result2": "Controlled sales funnel",
    "crm.result3": "Transparent analytics and KPIs",
    "crm.result4": "Synchronized team work",
    "crm.result5": "Integration with other systems",
    "crm.result6": "Automated routine tasks",
    "crm.result7": "Improved customer loyalty",
    "crm.result8": "Revenue growth",
    "crm.cta.title": "Ready to transform your customer management?",
    "crm.cta.description": "Let's discuss how a properly implemented CRM can drive your business forward.",
    "crm.cta.button": "Submit Request",

    // Automation Page
    "automation.hero.label": "Solutions",
    "automation.hero.title": "Process Automation",
    "automation.hero.description":
      "Eliminate manual work, reduce errors, and accelerate your business processes with intelligent automation.",
    "automation.overview.title": "Work Smarter, Not Harder",
    "automation.overview.description":
      "Every business has repetitive tasks that consume valuable time. We identify and automate these processes, allowing your team to focus on strategic work that drives growth.",
    "automation.overview.cta": "Submit Request",
    "automation.benefit1": "Reduce manual, repetitive tasks by up to 80%",
    "automation.benefit2": "Eliminate human errors in data entry and processing",
    "automation.benefit3": "Free up staff time for high-value activities",
    "automation.benefit4": "Accelerate process completion times",
    "automation.benefit5": "Improve consistency and compliance",
    "automation.benefit6": "Scale operations without proportional headcount",
    "automation.roi.title": "Economic Impact of Automation",
    "automation.roi.metric1.value": "80%",
    "automation.roi.metric1.label": "Reduction in manual processing time",
    "automation.roi.metric2.value": "99%",
    "automation.roi.metric2.label": "Accuracy in automated data handling",
    "automation.roi.metric3.value": "3-6mo",
    "automation.roi.metric3.label": "Typical payback period",
    "automation.roi.metric4.value": "35%+",
    "automation.roi.metric4.label": "Profit increase from process optimization",
    "automation.roi.footnote": "Based on implementation results at other companies",
    "automation.types.title": "Automation Capabilities",
    "automation.types.description": "Comprehensive automation solutions for every aspect of your operations.",
    "automation.type.workflow.title": "Workflow Automation",
    "automation.type.workflow.description": "Automation of any processes, any departments, any digital workplaces.",
    "automation.type.sales.title": "Sales Automation",
    "automation.type.sales.description": "Automate the entire sales cycle from lead to deal.",
    "automation.type.marketing.title": "Marketing Automation",
    "automation.type.marketing.description": "Automate campaigns, segmentation, and customer communications.",
    "automation.type.decisions.title": "Decision-Making Automation",
    "automation.type.decisions.description": "AI-powered systems for automated business decisions.",
    "automation.type.documents.title": "Document Flow Automation",
    "automation.type.documents.description": "Automate document creation, approval, and archiving.",
    "automation.type.analytics.title": "Data Collection & Analytics Automation",
    "automation.type.analytics.description": "Automated data gathering, processing, and analysis.",
    "automation.type.payments.title": "Payment Automation",
    "automation.type.payments.description": "Automate payment processing and reconciliation.",
    "automation.type.reporting.title": "Reporting Automation",
    "automation.type.reporting.description": "Automated report generation and distribution.",
    "automation.process.title": "Automation Stages",
    "automation.process.description": "A methodical approach to implementing lasting automation solutions.",
    "automation.process.step1.title": "Discovery",
    "automation.process.step1.description": "We map your current processes to identify automation opportunities.",
    "automation.process.step2.title": "Design",
    "automation.process.step2.description": "Creating automation workflows that align with your business logic.",
    "automation.process.step3.title": "Implementation",
    "automation.process.step3.description": "Building and testing automations with robust error handling.",
    "automation.process.step4.title": "Optimization",
    "automation.process.step4.description": "Continuous monitoring and refinement for maximum efficiency.",
    "automation.usecases.title": "Common Automation Use Cases",
    "automation.usecases.description": "Examples of processes we frequently automate for our clients.",
    "automation.usecase1": "Invoice processing and approval workflows",
    "automation.usecase2": "Customer onboarding sequences",
    "automation.usecase3": "Employee onboarding automation",
    "automation.usecase4": "Report generation and distribution",
    "automation.usecase5": "Data synchronization between systems",
    "automation.usecase6": "Notification and alert systems",
    "automation.usecase7": "Order processing and fulfillment",
    "automation.usecase8": "Contract and document management",
    "automation.cta.title": "Ready to automate your processes?",
    "automation.cta.description": "Let's identify opportunities to streamline your operations and boost productivity.",

    // BI Dashboards Page
    "bi.hero.label": "Solutions",
    "bi.hero.title": "BI Dashboards",
    "bi.hero.description": "Transform your data into actionable insights with custom business intelligence dashboards.",
    "bi.overview.title": "See Your Business Clearly",
    "bi.overview.description":
      "Your data tells a story. We build dashboards that make that story clear, actionable, and accessible to everyone who needs it—from executives to front-line managers.",
    "bi.benefit1": "Make faster, data-driven decisions",
    "bi.benefit2": "Identify trends and anomalies quickly",
    "bi.benefit3": "Share insights across the organization",
    "bi.benefit4": "Replace manual reporting with automation",
    "bi.benefit5": "Consolidate data from multiple sources",
    "bi.benefit6": "Track progress toward business goals",
    "bi.transform.title": "From Data Chaos to Clarity",
    "bi.transform.from1": "Scattered spreadsheets",
    "bi.transform.to1": "Unified dashboard",
    "bi.transform.from2": "Manual reports",
    "bi.transform.to2": "Real-time updates",
    "bi.transform.from3": "Static numbers",
    "bi.transform.to3": "Interactive visuals",
    "bi.transform.from4": "Hours of analysis",
    "bi.transform.to4": "Instant insights",
    "bi.types.title": "Dashboard Solutions",
    "bi.types.description": "Purpose-built dashboards for every area of your business.",
    "bi.type.executive.title": "Executive Dashboards",
    "bi.type.executive.description":
      "High-level KPI views for leadership with drill-down capabilities for deeper analysis.",
    "bi.type.sales.title": "Sales Analytics",
    "bi.type.sales.description": "Pipeline tracking, revenue forecasting, and sales performance metrics.",
    "bi.type.financial.title": "Financial Reporting",
    "bi.type.financial.description": "P&L visualization, budget tracking, and cash flow analysis dashboards.",
    "bi.type.operational.title": "Operational Metrics",
    "bi.type.operational.description": "Real-time monitoring of operations, inventory, and service delivery.",
    "bi.type.endtoend.title": "End-to-End Analytics",
    "bi.type.endtoend.description":
      "Combining data from all channels for a complete picture of marketing and sales effectiveness.",
    "bi.type.monitoring.title": "Company Monitoring",
    "bi.type.monitoring.description": "Real-time tracking of key business health indicators and team performance.",
    "bi.process.title": "Our Dashboard Development Process",
    "bi.process.description": "A collaborative approach to building dashboards that deliver value.",
    "bi.process.step1.title": "Data Audit",
    "bi.process.step1.description": "We assess your data sources, quality, and integration requirements.",
    "bi.process.step2.title": "Design",
    "bi.process.step2.description": "Creating dashboard layouts and visualizations aligned with your KPIs.",
    "bi.process.step3.title": "Development",
    "bi.process.step3.description": "Building interactive dashboards with real-time data connections.",
    "bi.process.step4.title": "Iteration",
    "bi.process.step4.description": "Refining based on user feedback and evolving business needs.",
    "bi.features.title": "Dashboard Features",
    "bi.features.description": "Modern BI capabilities built into every dashboard we create.",
    "bi.feature1": "Interactive, drill-down visualizations",
    "bi.feature2": "Real-time data refresh",
    "bi.feature3": "Mobile-responsive design",
    "bi.feature4": "Role-based access controls",
    "bi.feature5": "Automated report scheduling",
    "bi.feature6": "Data export capabilities",
    "bi.feature7": "Custom calculations and metrics",
    "bi.feature8": "Embedded analytics integration",
    "bi.feature.unified": "All key business metrics in one view",
    "bi.feature.realtime": "Real-time data updates",
    "bi.feature.drilldown": "Understanding the reasons behind numbers — details on click",
    "bi.feature.automation": "Automated reports without manual work",
    "bi.feature.security": "Role-based data access and security",
    "bi.feature.integration": "Integration with CRM and other systems",
    "bi.cta.title": "Ready to visualize your data?",
    "bi.cta.description": "Let's build dashboards that turn your data into competitive advantage.",

    // Custom Solutions Page
    "custom.hero.label": "Solutions",
    "custom.hero.title": "Custom IT Solutions for Business When Off-the-Shelf Doesn't Work",
    "custom.hero.description":
      "We design and implement targeted software solutions for specific business tasks: integrations, scoring, automation, custom logic, and data.",
    "custom.overview.title": "Built for Your Business",
    "custom.overview.description": "",
    "custom.overview.text": "",
    "custom.principle.title": "Our Principle:",
    "custom.principle1": "We don't rewrite platforms from scratch.",
    "custom.principle2": "We don't sell unnecessary development.",
    "custom.principle3": "We build exactly what the business needs — and only when it's essential.",
    "custom.quote": '"First — analysis. Then — integration. And only then — development."',
    "custom.approach.intro": "We start by answering the question:",
    "custom.approach.question": "can the task be solved without custom code?",
    "custom.approach.yes": "If yes — we'll tell you.",
    "custom.approach.no": "If no — we'll develop the optimal solution for your constraints and goals.",
    "custom.partnership.title": "Partnership Approach",
    "custom.partnership.description": "We work alongside your team, not just for you",
    "custom.when.title": "When You Need Us",
    "custom.when.reason1": "Standard software doesn't cover key processes",
    "custom.when.reason2": "Complex integration of multiple systems is required",
    "custom.when.reason3": "Unique decision-making logic is needed (scoring, rules, ML)",
    "custom.when.reason4": "You have data but no tools to extract value from it",
    "custom.when.reason5": "Off-the-shelf solution is expensive, redundant, or scales poorly",
    "custom.when.reason6": "Platform Extensions — Custom additions for existing software platforms",
    "custom.why.title": "Why Custom Development?",
    "custom.why.reason1.title": "Perfect Fit",
    "custom.why.reason1.description": "Software designed around your processes, not the other way around",
    "custom.why.reason2.title": "Competitive Edge",
    "custom.why.reason2.description": "Proprietary tools that competitors can't replicate",
    "custom.do.title": "What We Do",
    "custom.do.description": "Targeted solutions for specific business challenges.",
    "custom.do.local.title": "Local Custom Solutions",
    "custom.do.local.description":
      "We develop narrow, targeted modules for specific business tasks — without unnecessary functionality and technical debt.",
    "custom.do.integration.title": "Complex Integrations",
    "custom.do.integration.description":
      "We connect CRM, ERP, BI, payment systems, external APIs and legacy systems into a unified working environment.",
    "custom.do.scoring.title": "Scoring & Analytics Models",
    "custom.do.scoring.description":
      "We create risk, customer, operations and process assessment models — from business rules to ML algorithms.",
    "custom.do.automation.title": "Non-standard Process Automation",
    "custom.do.automation.description":
      "We digitize what can't be automated out-of-the-box: unique scenarios, exceptions, complex decision chains.",
    "custom.do.extensions.title": "Extending Existing Systems",
    "custom.do.extensions.description":
      "We build upon and enhance current IT solutions without complete platform replacement.",
    "custom.do.web.title": "Web Solutions",
    "custom.do.web.description": "Personal accounts, applications and other solutions for customer service.",
    "custom.do.cta.text":
      "Describe your case — we'll tell you where integration is enough and where custom development is essential.",
    "custom.do.cta.button": "Contact Us",
    "custom.types.title": "What We Build",
    "custom.types.description": "Custom solutions for diverse business needs.",
    "custom.type.apps.title": "Custom Applications",
    "custom.type.apps.description": "Bespoke web and mobile applications built to your exact specifications.",
    "custom.type.integration.title": "System Integration",
    "custom.type.integration.description": "Connecting disparate systems to create a unified technology ecosystem.",
    "custom.type.legacy.title": "Legacy Modernization",
    "custom.type.legacy.description":
      "Updating outdated systems with modern technology while preserving business logic.",
    "custom.type.extensions.title": "Platform Extensions",
    "custom.type.extensions.description": "Custom add-ons and extensions for your existing software platforms.",
    "custom.process.title": "How We Work",
    "custom.process.description": "A proven approach to delivering successful custom solutions.",
    "custom.process.step1.title": "Business Logic Analysis",
    "custom.process.step1.description": "Analysis of business logic, risks and regulatory requirements.",
    "custom.process.step2.title": "Architecture Design",
    "custom.process.step2.description": "Designing the solution architecture.",
    "custom.process.step3.title": "Ecosystem Integration",
    "custom.process.step3.description": "Integration with the current fintech ecosystem.",
    "custom.process.step4.title": "Custom Development",
    "custom.process.step4.description": "Development of custom modules.",
    "custom.process.step5.title": "Testing & Support",
    "custom.process.step5.description": "Testing, handover and ongoing support.",
    "custom.approach.title": "Our Approach",
    "custom.approach1": "Collaborative discovery workshops",
    "custom.approach2": "User-centered design methodology",
    "custom.approach3": "Agile, iterative development",
    "custom.approach4": "Comprehensive testing and QA",
    "custom.approach5": "Phased rollout strategies",
    "custom.approach6": "Knowledge transfer and documentation",
    "custom.support.title": "Ongoing Support",
    "custom.profitable.title": "Why It's Profitable for Business",
    "custom.profitable.cost.title": "Lower costs",
    "custom.profitable.cost.description": "Less expensive than building a platform from scratch",
    "custom.profitable.speed.title": "Faster implementation",
    "custom.profitable.speed.description": "Focus on one task means quicker results",
    "custom.profitable.logic.title": "Precise logic",
    "custom.profitable.logic.description": "Tailored to your business, not 'as the vendor intended'",
    "custom.profitable.control.title": "Full control",
    "custom.profitable.control.description": "Control over data and processes",
    "custom.profitable.scale.title": "Scalability",
    "custom.profitable.scale.description": "Scale without rebuilding",
    "custom.profitable.vendor.title": "Vendor independence",
    "custom.profitable.vendor.description": "You are not tied to roadmaps, licenses and vendor restrictions",
    "custom.support1": "Dedicated support and maintenance",
    "custom.support2": "Performance monitoring and optimization",
    "custom.support3": "Feature enhancements and updates",
    "custom.support4": "Scalability planning and implementation",
    "custom.support5": "Security patches and compliance updates",
    "custom.support6": "Regular review and roadmap sessions",
    "custom.cta.title": "Have a unique challenge?",
    "custom.cta.description": "Let's explore how a custom solution can give you the edge you need.",

    // Contact Form Titles per page
    "form.smm.title": "Ready to grow your social media presence?",
    "form.smm.description": "Let's create a strategy that connects with your audience and delivers results.",
    "form.research.title": "Need insights for your strategy?",
    "form.research.description": "Let's discuss how market research can help your business decisions.",
    "form.crm.title": "Ready to transform business management?",
    "form.crm.description": "Let's discuss how a properly implemented CRM can advance your business.",
    "form.automation.title": "Ready to automate processes?",
    "form.automation.description": "Let's find opportunities to optimize operations and increase productivity.",
    "form.bi.title": "Ready to implement BI analytics?",
    "form.bi.description": "Let's create dashboards that turn your data into a competitive advantage!",
  },
  ru: {
    // Common
    "common.start": "Начать",
    "common.submitRequest": "Отправить заявку",

    // Header
    "nav.marketing": "Маркетинг",
    "nav.solutions": "Решения",
    "nav.contact": "Контакты",
    "nav.callback": "Заказать звонок",
    "nav.smm": "SMM",
    "nav.smm.desc": "Маркетинг в социальных сетях",
    "nav.research": "Исследование рынка",
    "nav.research.desc": "Аналитика на основе данных",
    "nav.crm": "Внедрение CRM",
    "nav.crm.desc": "Система управления бизнесом",
    "nav.automation": "Автоматизация процессов",
    "nav.automation.desc": "Оптимизация рабочих процессов",
    "nav.bi": "BI Дашборды",
    "nav.bi.desc": "Бизнес-аналитика",
    "nav.custom": "Индивидуальные решения",
    "nav.custom.desc": "Заказная разработка",

    // Callback Modal
    "callback.title": "Заказать звонок",
    "callback.description": "Оставьте ваши данные и мы перезвоним в течение 15 минут",
    "callback.name": "Имя",
    "callback.namePlaceholder": "Ваше имя",
    "callback.phone": "Телефон",
    "callback.sending": "Отправка...",
    "callback.success": "Заявка отправлена!",
    "callback.successDesc": "Мы перезвоним вам в течение 15 минут",
    "callback.error": "Ошибка",
    "callback.fillFields": "Пожалуйста, заполните все поля корректно",

    // Hero
    "hero.badge": "Цифровой бизнес-партнёр",
    "hero.title1": "Binar+",
    "hero.title2": "надежный партнер по цифровой трансформации бизнеса",
    "hero.description":
      "Мы помогаем бизнесу внедрять стратегические маркетинговые и технологические решения, которые обеспечивают измеримый рост.",
    "hero.feature1": "CRM и ERP интеграция",
    "hero.feature2": "Автоматизация процессов",
    "hero.feature3": "BI аналитика",
    "hero.cta": "Начать",
    "hero.ctaSecondary": "Смотреть решения",

    // Sections
    "marketing.label": "Маркетинг",
    "marketing.title": "Стратегические маркетинговые услуги",
    "marketing.description":
      "Достигните целевой аудитории и создайте устойчивую узнаваемость бренда с нашими комплексными маркетинговыми решениями.",

    // SMM Homepage Section
    "smm.label": "Маркетинг",
    "smm.title": "SMM Маркетинг",
    "smm.description":
      "Мы помогаем бизнесу развивать присутствие в социальных сетях. Наша команда создаёт вовлекающий контент, ведёт рекламные кампании и строит активные сообщества в Instagram, Facebook, TikTok и других платформах.",
    "smm.home.feature1": "Разработка контент-стратегии",
    "smm.home.feature2": "Таргетированные рекламные кампании",
    "smm.home.feature3": "Управление сообществом",
    "smm.home.feature4": "Аналитика и отчётность",
    "smm.cta": "Подробнее",

    // Market Research Homepage Section
    "research.label": "Маркетинг",
    "research.title": "Маркетинговые исследования",
    "research.description":
      "Мы проводим комплексные маркетинговые исследования, чтобы помочь вам понять вашу аудиторию, конкурентов и тенденции рынка. Наши аналитические данные позволяют принимать более эффективные бизнес-решения.",
    "research.home.feature1": "Анализ поведения потребителей",
    "research.home.feature2": "Конкурентная разведка",
    "research.home.feature3": "Прогнозирование рыночных трендов",
    "research.home.feature4": "Рекомендации на основе данных",
    "research.cta": "Подробнее",

    // Budget Request Section
    "budget.promo.banner": "Аудит SMM БЕСПЛАТНО до 01.03.2026!",
    "budget.label": "Маркетинговые решения",
    "budget.title": "Закажите Аудит и стратегию для SMM",
    "budget.description":
      "Получите персональную оценку маркетингового бюджета и узнайте, как наш интегрированный технологический стек может трансформировать ваш бизнес.",
    "budget.feature1": "Полная интеграция с CRM, ERP и BI системами",
    "budget.feature2": "Автоматизированные маркетинговые процессы",
    "budget.feature3": "Аналитика и отчётность в реальном времени",
    "budget.form.title": "Получить расчёт",
    "budget.form.name": "Имя",
    "budget.form.namePlaceholder": "Ваше имя",
    "budget.form.phone": "Телефон",
    "budget.form.email": "Email",
    "budget.form.calculate": "Заказать",
    "budget.form.sending": "Отправка...",
    "budget.form.success": "Заявка отправлена!",
    "budget.form.successDescription": "Наш специалист свяжется с вами в течение 24 часов.",
    "budget.form.error": "Ошибка",
    "budget.form.errorDescription": "Пожалуйста, заполните обязательные поля.",

    // Systems Implementation Section
    "systems.label": "Решения",
    "systems.title": "Внедрение CRM, BPM, ERP",
    "systems.description":
      "Мы внедряем и настраиваем системы управления предприятием, которые оптимизируют бизнес-процессы, повышают эффективность и обеспечивают полный контроль над деятельностью компании.",
    "systems.home.feature1": "Подбор и внедрение системы под ваш бизнес",
    "systems.home.feature2": "Настройка системы под ваши бизнес-задачи",
    "systems.home.feature3": "Интеграция с необходимыми системами",
    "systems.home.feature4": "Обучение персонала работе в системе",
    "systems.cta": "Подробнее",

    // Automation Homepage Section
    "automation.label": "Решения",
    "automation.title": "Автоматизация бизнес-процессов",
    "automation.description":
      "Мы внедряем передовые решения по автоматизации с использованием роботизации процессов (RPA), AI-агентов, машинного обучения и интеллектуальных рабочих процессов для трансформации вашего бизнеса.",
    "automation.home.feature1": "Роботизация процессов (RPA)",
    "automation.home.feature2": "AI-агенты и виртуальные ассистенты",
    "automation.home.feature3": "Интеллектуальная обработка документов",
    "automation.home.feature4": "No-code/low-code платформы автоматизации",
    "automation.cta": "Подробнее",

    // BI Homepage Section
    "bi.label": "Решения",
    "bi.title": "Бизнес-аналитика и BI-дашборды",
    "bi.description":
      "Мы создаём интерактивные дашборды и аналитические решения, которые превращают ваши данные в понятные инсайты для принятия стратегических решений.",
    "bi.home.feature1": "Интерактивные дашборды Power BI",
    "bi.home.feature2": "Визуализация данных в реальном времени",
    "bi.home.feature3": "Мониторинг KPI и отчётность",
    "bi.home.feature4": "Интеграция данных из разных источников",
    "bi.cta": "Подробнее",

    // Custom Solutions Homepage Section
    "custom.label": "Решения",
    "custom.home.title": "Индивидуальные решения",
    "custom.home.description":
      "Мы разрабатываем заказное программное обеспечение под уникальные потребности вашего бизнеса — от сайтов и мобильных приложений до систем принятия решений (скоринг) и комплексных корпоративных решений.",
    "custom.home.feature1": "Корпоративные сайты и веб-приложения",
    "custom.home.feature2": "Мобильные приложения для iOS и Android",
    "custom.home.feature3": "Скоринговые и системы принятия решений",
    "custom.home.feature4": "Интеграции и разработка кастомных API",
    "custom.cta": "Подробнее",

    // About Company Section
    "about.label": "О компании",
    "about.title": "Почему выбирают Binar+",
    "about.description":
      "Мы входим в финтех группу компаний с обширным опытом создания высоконагруженных систем для бизнеса на рынках ЕС и СНГ.",
    "about.advantage1": "Лет в деле",
    "about.advantage2": "Опыт на рынках ЕС и СНГ",
    "about.advantage3": "Часть финтех-группы",
    "about.advantage4": "Высоконагруженных проектов",

    // Principles Section
    "principles.label": "Наши принципы",
    "principles.title": "Как мы работаем",
    "principles.principle1.title": "Всегда доводим до конца",
    "principles.principle1.description":
      "Мы доводим каждый проект до завершения. Какими бы ни были трудности, мы находим решения и достигаем результата.",
    "principles.principle2.title": "Отвечаем за результат",
    "principles.principle2.description":
      "Мы стоим за своей работой и несём ответственность за результаты. Ваш успех — это наш критерий успеха.",
    "principles.principle3.title": "Понимаем бизнес",
    "principles.principle3.description":
      "Мы помогаем строить бизнес системы, учитывая особенности бизнеса клиента с обязательной оглядкой на требования законодательства и стандарты безопасности.",

    // Testimonials Section
    "testimonials.label": "Отзывы",
    "testimonials.title": "Что говорят наши клиенты",
    "testimonials.description": "Мы гордимся работой с ведущими компаниями на рынках ЕС, СНГ и Центральной Азии.",
    "testimonials.country.belarus": "Беларусь",
    "testimonials.country.russia": "Россия",
    "testimonials.country.kazakhstan": "Казахстан",
    "testimonials.review1":
      "Binar+ внедрил комплексную CRM-систему, которая повысила эффективность нашего отдела продаж на 40%. Их экспертиза в финтехе не имеет равных.",
    "testimonials.review2":
      "Решения по автоматизации от Binar+ помогли нам сократить время ручной обработки на 60%. Высокопрофессиональная команда.",
    "testimonials.review3":
      "Отличные BI-дашборды, которые дают нам аналитику лизингового портфеля в реальном времени. Это изменило наш подход к принятию решений.",
    "testimonials.review4":
      "Работа с Binar+ над цифровой трансформацией прошла безупречно. Они понимают регуляторику и требования безопасности финтеха.",
    "testimonials.review5":
      "Скоринговая система, разработанная Binar+, значительно повысила точность наших кредитных решений. Отличная техническая экспертиза.",
    "testimonials.review6":
      "Их решения по автоматизации процессов помогли нам масштабировать операции без пропорционального увеличения штата.",
    "testimonials.review7":
      "Binar+ реализовал полную интеграцию ERP, объединив все наши бизнес-процессы. Профессиональный и надёжный партнёр.",
    "testimonials.review8":
      "Платформа маркетинговой аналитики, которую они создали, даёт нам беспрецедентную видимость воронки привлечения клиентов.",
    "testimonials.review9":
      "От консультации до внедрения Binar+ продемонстрировал глубокое понимание как технологий, так и бизнес-потребностей.",
    "testimonials.author1": "Александр Петров",
    "testimonials.position1": "Технический директор",
    "testimonials.author2": "Мария Иванова",
    "testimonials.position2": "Директор по операциям",
    "testimonials.author3": "Дмитрий Козлов",
    "testimonials.position3": "Генеральный директор",
    "testimonials.author4": "Елена Смирнова",
    "testimonials.position4": "Руководитель Digital",
    "testimonials.author5": "Сергей Волков",
    "testimonials.position5": "IT-директор",
    "testimonials.author6": "Анна Кузнецова",
    "testimonials.position6": "Операционный директор",
    "testimonials.author7": "Нурсултан Ахметов",
    "testimonials.position7": "Управляющий директор",
    "testimonials.author8": "Аида Нурбекова",
    "testimonials.position8": "Директор по маркетингу",
    "testimonials.author9": "Бекзат Есенов",
    "testimonials.position9": "Генеральный директор",

    "solutions.label": "Решения",
    "solutions.title": "Технологические решения",
    "solutions.description":
      "Используйте передовые технологии для оптимизации операций, повышения эффективности и ускорения роста бизнеса.",

    // Services
    "service.smm.title": "Маркетинг в социальных сетях",
    "service.smm.description":
      "Развивайте присутствие бренда и вовлекайте аудиторию на всех основных социальных платформах с помощью стратегий на основе данных.",
    "service.research.title": "Исследование рынка",
    "service.research.description":
      "Получите глубокое понимание рынка, конкурентов и клиентов для принятия обоснованных бизнес-решений.",
    "service.crm.title": "Внедрение CRM",
    "service.crm.description":
      "Внедряйте и настраивайте CRM-системы, которые оптимизируют отношения с клиентами и повышают эффективность продаж.",
    "service.automation.title": "Автоматизация процессов",
    "service.automation.description":
      "Автоматизируйте повторяющиеся задачи и рабочие процессы для повышения производительности и снижения операционных затрат.",
    "service.bi.title": "BI Дашборды",
    "service.bi.description":
      "Превратите данные в практические инсайты с помощью индивидуальных дашбордов бизнес-аналитики.",
    "service.custom.title": "Индивидуальные решения",
    "service.custom.description":
      "Получите программные решения, разработанные специально для ваших уникальных бизнес-задач.",
    "service.learnMore": "Подробнее",

    // CTA Banner
    "cta.title": "Готовы трансформировать свой бизнес?",
    "cta.description": "Давайте обсудим, как наши решения могут помочь вам достичь ваших целей.",
    "cta.button": "Связаться с нами",

    // Footer
    "footer.about":
      "Мы помогаем бизнесу через цифровую трансформацию. Сочетаем стратегическое мышление с техническим опытом для достижения роста.",
    "footer.privacy": "Политика конфиденциальности",
    "footer.terms": "Условия использования",
    "footer.rights": "Все права защищены.",
    "footer.address.line1": "Республика Узбекистан, 100170,",
    "footer.address.line2": "г. Ташкент, Мирзо-Улугбекский район,",
    "footer.address.line3": "ул. Зиёлилар, д. 26, 1-ый этаж (lounge hall)",
    "cookie.title": "Мы используем cookie",
    "cookie.description":
      "Этот сайт использует файлы cookie для улучшения вашего опыта и анализа трафика. Продолжая использовать сайт, вы соглашаетесь с использованием cookie.",
    "cookie.accept": "Принять",
    "cookie.decline": "Отклонить",
    "footer.form.title": "Готовы трансформировать свой бизнес?",
    "footer.form.description": "Давайте обсудим, как наши решения могут помочь вам достичь ваших целей.",
    "footer.form.company": "Название компании",
    "footer.form.companyPlaceholder": "Название вашей компании",
    "footer.form.message": "Сообщение",
    "footer.form.messagePlaceholder": "Расскажите о вашем проекте...",
    "footer.form.submit": "Отправить заявку",

    // Contact Page
    "contact.title": "Давайте работать вместе",
    "contact.subtitle":
      "Готовы трансформировать свой бизнес? Свяжитесь с нами, и давайте обсудим, как мы можем помочь вам достичь ваших целей.",
    "contact.form.title": "Отправьте нам сообщение",
    "contact.form.description": "Заполните форму ниже, и мы свяжемся с вами в течение одного рабочего дня.",
    "contact.form.name": "Имя",
    "contact.form.name.placeholder": "Ваше имя",
    "contact.form.email": "Email",
    "contact.form.email.placeholder": "ваш@email.com",
    "contact.form.company": "Компания",
    "contact.form.company.placeholder": "Ваша компания",
    "contact.form.service": "Интересующая услуга",
    "contact.form.service.placeholder": "Выберите услугу",
    "contact.form.message": "Сообщение",
    "contact.form.message.placeholder": "Расскажите о вашем проекте или задайте вопрос...",
    "contact.form.submit": "Отправить",
    "contact.form.sending": "Отправка...",
    "contact.form.success.title": "Сообщение отправлено!",
    "contact.form.success.description": "Мы свяжемся с вами в ближайшее время.",
    "contact.info.title": "Контактная информация",
    "contact.info.email": "Email",
    "contact.info.phone": "Телефон",
    "contact.info.office": "Офис",
    "contact.social.title": "Мы в соцсетях",
    "contact.map.title": "Как нас найти",

    // Services dropdown
    "service.smm.label": "Маркетинг в соцсетях",
    "service.research.label": "Исследование рынка",
    "service.crm.label": "Внедрение CRM",
    "service.automation.label": "Автоматизация процессов",
    "service.bi.label": "BI Дашборды",
    "service.custom.label": "Индивидуальные решения",
    "service.other.label": "Другое",

    // SMM Page
    "smm.hero.label": "Маркетинг",
    "smm.hero.title": "Маркетинг в социальных сетях",
    "smm.hero.description":
      "Создавайте значимые связи с аудиторией и развивайте присутствие бренда на всех основных социальных платформах.",
    "smm.overview.title": "Взаимодействуйте с аудиторией там, где она находится",
    "smm.overview.description":
      "SMM-маркетинг в Узбекистане — это один из самых эффективных способов продвижения бизнеса в интернете. Мы работаем не ради лайков, а ради реальных заявок, клиентов и продаж.",
    "smm.overview.benefit1": "Аудит и оценка целевой аудитории",
    "smm.overview.benefit2": "Разработка SMM-стратегии под ваш бизнес",
    "smm.overview.benefit3": "Упаковка и оформление аккаунтов",
    "smm.overview.benefit4": "Создание продающего и вовлекающего контента",
    "smm.overview.benefit5": "Запуск и оптимизация таргетированной рекламы",
    "smm.overview.benefit6": "Аналитика, тестирование и масштабирование результата",
    "smm.overview.cta": "Отправить заявку",
    "smm.process.title": "Этапы работ",
    "smm.process.description": "Проверенный подход к созданию и реализации успешных стратегий в социальных сетях.",
    "smm.process.step1.title": "Анализ бизнеса",
    "smm.process.step1.description": "Изучаем нишу, целевую аудиторию, конкурентов и текущее состояние аккаунтов.",
    "smm.process.step2.title": "Стратегия продвижения",
    "smm.process.step2.description": "Определяем цели, формат контента, воронку продаж и рекламную модель.",
    "smm.process.step3.title": "Упаковка аккаунта",
    "smm.process.step3.description": "Оформляем профиль так, чтобы он вызывал доверие и мотивировал написать вам.",
    "smm.process.step4.title": "Контент и реклама",
    "smm.process.step4.description":
      "Регулярно публикуем контент + запускаем таргетированную рекламу с тестами гипотез.",
    "smm.process.step5.title": "Аналитика и рост",
    "smm.process.step5.description": "Отслеживаем показатели, усиливаем рабочие связки и увеличиваем результат.",
    "smm.features.title": "Результат в конечном итоге",
    "smm.features.description": "Социальные сети начинают работать на ваш бизнес, а не просто существовать.",
    "smm.feature1": "Рост охватов и узнаваемости бренда",
    "smm.feature2": "Стабильный поток заявок из соцсетей",
    "smm.feature3": "Живая и заинтересованная аудитория",
    "smm.feature4": "Снижение стоимости клиента (лида)",
    "smm.feature5": "Понятная и управляемая система привлечения клиентов",
    "smm.cta.title": "Готовы развивать присутствие в соцсетях?",
    "smm.cta.description": "Давайте создадим стратегию, которая свяжет вас с аудиторией и принесет результаты.",
    "smm.platform.linkedin": "LinkedIn",
    "smm.platform.linkedin.desc": "B2B нетворкинг и лидерство мнений",
    "smm.platform.instagram": "Instagram",
    "smm.platform.instagram.desc": "Визуальный сторителлинг и узнаваемость",
    "smm.platform.facebook": "Facebook",
    "smm.platform.facebook.desc": "Построение сообщества и реклама",
    "smm.platform.twitter": "Twitter/X",
    "smm.platform.twitter.desc": "Взаимодействие в реальном времени",

    // Research Page
    "research.hero.label": "Маркетинг",
    "research.hero.title": "Исследование рынка",
    "research.hero.description":
      "Принимайте уверенные бизнес-решения на основе комплексной рыночной аналитики и понимания клиентов.",
    "research.overview.title": "Инсайты, которые стимулируют рост",
    "research.overview.description":
      "Понимание рынка — основа успеха бизнеса. Наши исследования сочетают строгую методологию с практическим бизнес-опытом.",
    "research.overview.text":
      "Выходите ли вы на новый рынок, запускаете продукт или хотите лучше понять клиентов — мы предоставляем данные и анализ для уверенного движения вперед.",
    "research.overview.benefit1": "Первичные и вторичные исследования",
    "research.overview.benefit2": "Количественные и качественные методологии",
    "research.overview.benefit3": "Отраслевая экспертиза",
    "research.overview.benefit4": "Практические стратегические рекомендации",
    "research.decisions.title": "Как исследования влияют на решения",
    "research.decisions.step1.title": "Определить вопрос",
    "research.decisions.step1.desc": "Какое решение нужно принять?",
    "research.decisions.step2.title": "Собрать данные",
    "research.decisions.step2.desc": "Какие данные поддерживают каждый вариант?",
    "research.decisions.step3.title": "Анализ и рекомендации",
    "research.decisions.step3.desc": "Что говорят данные?",
    "research.decisions.step4.title": "Действовать уверенно",
    "research.decisions.step4.desc": "Двигаться вперед со сниженным риском.",
    "research.types.title": "Услуги исследований",
    "research.types.description": "Комплексные исследовательские решения для ваших бизнес-потребностей.",
    "research.type.customer.title": "Исследование клиентов",
    "research.type.customer.description":
      "Понимание потребностей, предпочтений и болевых точек клиентов через опросы, интервью и поведенческий анализ.",
    "research.type.competitor.title": "Анализ конкурентов",
    "research.type.competitor.description":
      "Карта конкурентной среды, выявление рыночных пробелов и бенчмаркинг относительно лидеров отрасли.",
    "research.type.sizing.title": "Оценка рынка",
    "research.type.sizing.description":
      "Количественная оценка рыночных возможностей, потенциала и определение сегментов роста.",
    "research.type.strategy.title": "Разработка стратегий",
    "research.type.strategy.description":
      "Разработка комплексных стратегий по продукту, маркетингу, персоналу, финансам и инвестициям на основе рыночных данных.",
    "research.process.title": "Наш исследовательский процесс",
    "research.process.description": "Системный подход к получению практических рыночных инсайтов.",
    "research.process.step1.title": "Определение целей",
    "research.process.step1.description": "Работаем с вами над выявлением ключевых вопросов и целей исследования.",
    "research.process.step2.title": "Сбор данных",
    "research.process.step2.description": "Сбор качественных и количественных данных проверенными методами.",
    "research.process.step3.title": "Анализ",
    "research.process.step3.description": "Аналитики извлекают значимые паттерны и практические инсайты.",
    "research.process.step4.title": "Рекомендации",
    "research.process.step4.description": "Четкие стратегические рекомендации для принятия бизнес-решений.",
    "research.benefits.title": "Преимущества исследования рынка",
    "research.benefits.description": "Инвестируйте в понимание рынка для получения конкурентного преимущества.",
    "research.benefit1": "Снижение рисков при принятии стратегических решений",
    "research.benefit2": "Выявление новых рыночных возможностей",
    "research.benefit3": "Понимание потребностей и ожиданий клиентов",
    "research.benefit4": "Бенчмаркинг относительно конкурентов",
    "research.benefit5": "Проверка идей, продуктов и услуг перед запуском",
    "research.benefit6": "Оптимизация ценовых стратегий",
    "research.benefit7": "Повышение удовлетворенности и лояльности клиентов",
    "research.benefit8": "Поддержка планирования инвестиций и роста",
    "research.cta.title": "Нужны инсайты для вашей стратегии?",
    "research.cta.description": "Давайте обсудим, как исследование рынка может помочь вашим бизнес-решениям.",

    // CRM Page
    "crm.hero.label": "Решения",
    "crm.hero.title": "Внедрение CRM",
    "crm.hero.description": "Трансформируйте ваш бизнес с помощью CRM-системы, адаптированной под ваши потребности.",
    "crm.overview.title": "Постройте эффективную систему управления бизнесом",
    "crm.overview.description":
      "Правильно внедренная CRM — основа успешного управления бизнесом. Мы помогаем выбрать, настроить и развернуть CRM-решения.",
    "crm.benefit1": "360-градусный обзор бизнес-процессов",
    "crm.benefit2": "Повышение продуктивности отдела продаж",
    "crm.benefit3": "Улучшение точности прогнозирования",
    "crm.benefit4": "Улучшение клиентского сервиса",
    "crm.benefit5": "Оптимизация коммуникаций",
    "crm.benefit6": "Принятие решений на основе данных",
    "crm.why.title": "Почему важно профессиональное внедрение",
    "crm.why.text1":
      "Многие внедрения CRM терпят неудачу не из-за технологий, а из-за плохого планирования или недостаточной адаптации.",
    "crm.why.text2":
      "Наш подход обеспечивает настройку CRM под реальную работу вашей команды с чистой миграцией данных и обучением.",
    "crm.why.result":
      "Результат: CRM, которую ваша команда действительно использует и которая приносит реальную ценность.",
    "crm.services.title": "Наши CRM-услуги",
    "crm.services.description": "Комплексная поддержка внедрения CRM для вашего бизнеса.",
    "crm.service.selection.title": "Выбор системы",
    "crm.service.selection.description":
      "Помогаем выбрать правильную CRM-платформу на основе отрасли, размера и требований.",
    "crm.service.config.title": "Индивидуальная настройка",
    "crm.service.config.description": "Настройка полей, рабочих процессов, автоматизаций и интеграций.",
    "crm.service.integration.title": "Интеграция",
    "crm.service.integration.description":
      "Подключение CRM к email, маркетинговым инструментам, бухгалтерии и другим системам.",
    "crm.service.support.title": "Постоянная поддержка",
    "crm.service.support.description":
      "Непрерывная оптимизация, устранение проблем и обновления по мере развития бизнеса.",
    "crm.process.title": "Процесс внедрения",
    "crm.process.description": "Структурированный подход к успешному развертыванию CRM.",
    "crm.process.step1.title": "Оценка",
    "crm.process.step1.description": "Анализируем текущие процессы и определяем идеальное CRM-решение.",
    "crm.process.step2.title": "Настройка",
    "crm.process.step2.description": "Адаптация CRM под ваши рабочие процессы, структуру данных и бизнес-правила.",
    "crm.process.step3.title": "Миграция",
    "crm.process.step3.description": "Безопасный перенос существующих данных с валидацией и очисткой.",
    "crm.process.step4.title": "Обучение",
    "crm.process.step4.description": "Комплексное обучение для максимального использования потенциала CRM.",
    "crm.results.title": "Результат после внедрения",
    "crm.results.description":
      "Компания получает не просто CRM, а централизованный инструмент управления результатами.",
    "crm.result1": "Единая база клиентов",
    "crm.result2": "Контролируемая воронка продаж",
    "crm.result3": "Прозрачная аналитика и KPI",
    "crm.result4": "Синхронизированная работа команды",
    "crm.result5": "Интеграция с другими системами",
    "crm.result6": "Автоматизация рутинных задач",
    "crm.result7": "Повышение лояльности клиентов",
    "crm.result8": "Рост выручки",
    "crm.cta.title": "Готовы трансформировать управление бизнесом?",
    "crm.cta.description": "Давайте обсудим, как правильно внедренная CRM может продвинуть ваш бизнес.",
    "crm.cta.button": "Отправить заявку",

    // Automation Page
    "automation.hero.label": "Решения",
    "automation.hero.title": "Автоматизация процессов",
    "automation.hero.description":
      "Устраните ручную работу, сократите ошибки и ускорьте бизнес-процессы с помощью интеллектуальной автоматизации.",
    "automation.overview.title": "Работайте умнее, а не больше",
    "automation.overview.description":
      "В каждом бизнесе есть повторяющиеся задачи. Мы находим и автоматизируем эти процессы, освобождая время для стратегической работы.",
    "automation.overview.cta": "Отправить заявку",
    "automation.benefit1": "Сокращение ручных задач до 80%",
    "automation.benefit2": "Устранение человеческих ошибок при вводе данных",
    "automation.benefit3": "Высвобождение времени для ценных задач",
    "automation.benefit4": "Ускорение выполнения процессов",
    "automation.benefit5": "Улучшение согласованности и соответствия",
    "automation.benefit6": "Масштабирование без пропорционального найма",
    "automation.roi.title": "Экономический эффект от автоматизации",
    "automation.roi.metric1.value": "80%",
    "automation.roi.metric1.label": "Сокращение времени ручной обработки",
    "automation.roi.metric2.value": "99%",
    "automation.roi.metric2.label": "Точность автоматической обработки данных",
    "automation.roi.metric3.value": "3-6 мес",
    "automation.roi.metric3.label": "Типичный срок окупаемости",
    "automation.roi.metric4.value": "35%+",
    "automation.roi.metric4.label": "Рост прибыли от оптимизации процессов",
    "automation.roi.footnote": "По результатам внедрений в другие компании",
    "automation.types.title": "Возможности автоматизации",
    "automation.types.description": "Комплексные решения автоматизации для всех аспектов операций.",
    "automation.type.workflow.title": "Автоматизация рабочих процессов",
    "automation.type.workflow.description":
      "Автоматизация любых процессов, любых подразделений, любых цифровых рабочих мест.",
    "automation.type.sales.title": "Автоматизация продаж",
    "automation.type.sales.description": "Автоматизация полного цикла продаж от лида до сделки.",
    "automation.type.marketing.title": "Автоматизация маркетинга",
    "automation.type.marketing.description": "Автоматизация кампаний, сегментации и коммуникаций с клиентами.",
    "automation.type.decisions.title": "Автоматизация принятия решений",
    "automation.type.decisions.description": "AI-системы для автоматизированных бизнес-решений.",
    "automation.type.documents.title": "Автоматизация документооборота",
    "automation.type.documents.description": "Автоматизация создания, согласования и архивирования документов.",
    "automation.type.analytics.title": "Автоматизация сбора и аналитики данных",
    "automation.type.analytics.description": "Автоматический сбор, обработка и анализ данных.",
    "automation.type.payments.title": "Автоматизация платежей",
    "automation.type.payments.description": "Автоматизация обработки и сверки платежей.",
    "automation.type.reporting.title": "Автоматизация отчётности",
    "automation.type.reporting.description": "Автоматическое формирование и рассылка отчётов.",
    "automation.process.title": "Этапы автоматизации",
    "automation.process.description": "Методичный подход к внедрению устойчивых решений автоматизации.",
    "automation.process.step1.title": "Исследование",
    "automation.process.step1.description": "Картируем текущие процессы для выявления возможностей автоматизации.",
    "automation.process.step2.title": "Проектирование",
    "automation.process.step2.description": "Создание рабочих процессов автоматизации в соответствии с бизнес-логикой.",
    "automation.process.step3.title": "Внедрение",
    "automation.process.step3.description": "Построение и тестирование автоматизаций с надежной обработкой ошибок.",
    "automation.process.step4.title": "Оптимизация",
    "automation.process.step4.description": "Непрерывный мониторинг и улучшение для максимальной эффективности.",
    "automation.usecases.title": "Типичные сценарии автоматизации",
    "automation.usecases.description": "Примеры процессов, которые мы часто автоматизируем для клиентов.",
    "automation.usecase1": "Обработка счетов и согласования",
    "automation.usecase2": "Последовательности онбординга клиентов",
    "automation.usecase3": "Автоматизация онбординга сотрудников",
    "automation.usecase4": "Генерация и рассылка отчетов",
    "automation.usecase5": "Синхронизация данных между системами",
    "automation.usecase6": "Системы уведомлений и оповещений",
    "automation.usecase7": "Обработка и выполнение заказов",
    "automation.usecase8": "Управление контрактами и документами",
    "automation.cta.title": "Готовы автоматизировать процессы?",
    "automation.cta.description": "Давайте найдем возможности для оптимизации операций и повышения продуктивности.",

    // BI Dashboards Page
    "bi.hero.label": "Решения",
    "bi.hero.title": "BI Дашборды",
    "bi.hero.description":
      "Превратите данные в практические инсайты с помощью индивидуальных дашбордов бизнес-аналитики.",
    "bi.overview.title": "Видьте свой бизнес ясно",
    "bi.overview.description":
      "Ваши данные рассказывают историю. Мы создаем дашборды, которые делают эту историю понятной и доступной для всех.",
    "bi.benefit1": "Быстрое принятие решений на основе данных",
    "bi.benefit2": "Быстрое выявление трендов и аномалий",
    "bi.benefit3": "Распространение инсайтов по организации",
    "bi.benefit4": "Замена ручной отчетности автоматизацией",
    "bi.benefit5": "Консолидация данных из нескольких источников",
    "bi.benefit6": "Отслеживание прогресса к бизнес-целям",
    "bi.transform.title": "От хаоса данных к ясности",
    "bi.transform.from1": "Разрозненные таблицы",
    "bi.transform.to1": "Единый дашборд",
    "bi.transform.from2": "Ручные отчеты",
    "bi.transform.to2": "Обновления в реальном времени",
    "bi.transform.from3": "Статичные числа",
    "bi.transform.to3": "Интерактивные визуализации",
    "bi.transform.from4": "Часы анализа",
    "bi.transform.to4": "Мгновенные инсайты",
    "bi.types.title": "Решения дашбордов",
    "bi.types.description": "Целевые дашборды для каждой области бизнеса.",
    "bi.type.executive.title": "Дашборды для руководства",
    "bi.type.executive.description": "Высокоуровневые KPI с возможностью детализации для глубокого анализа.",
    "bi.type.sales.title": "Аналитика продаж",
    "bi.type.sales.description": "Отслеживание воронки, прогнозирование выручки и метрики эффективности продаж.",
    "bi.type.financial.title": "Финансовая отчетность",
    "bi.type.financial.description": "Визуализация P&L, отслеживание бюджета и анализ денежных потоков.",
    "bi.type.operational.title": "Операционные метрики",
    "bi.type.operational.description": "Мониторинг операций, запасов и качества обслуживания в реальном времени.",
    "bi.type.endtoend.title": "Сквозная аналитика",
    "bi.type.endtoend.description":
      "Объединение данных из всех каналов для полной картины эффективности маркетинга и продаж.",
    "bi.type.monitoring.title": "Мониторинг компании",
    "bi.type.monitoring.description":
      "Отслеживание ключевых показателей здоровья бизнеса и производительности команд в реальном времени.",
    "bi.process.title": "Процесс разработки дашбордов",
    "bi.process.description": "Совместный подход к созданию дашбордов, приносящих ценность.",
    "bi.process.step1.title": "Аудит данных",
    "bi.process.step1.description": "Оцениваем источники данных, качество и требования к интеграции.",
    "bi.process.step2.title": "Дизайн",
    "bi.process.step2.description": "Создание макетов и визуализаций в соответствии с вашими KPI.",
    "bi.process.step3.title": "Разработка",
    "bi.process.step3.description": "Построение интерактивных дашбордов с подключением данных в реальном времени.",
    "bi.process.step4.title": "Итерация",
    "bi.process.step4.description": "Доработка на основе обратной связи и развивающихся потребностей.",
    "bi.features.title": "Функции дашбордов",
    "bi.features.description": "Современные BI-возможности в каждом создаваемом дашборде.",
    "bi.feature1": "Интерактивные визуализации с детализацией",
    "bi.feature2": "Обновление данных в реальном времени",
    "bi.feature3": "Адаптивный дизайн для мобильных",
    "bi.feature4": "Управление доступом по ролям",
    "bi.feature5": "Автоматическое планирование отчетов",
    "bi.feature6": "Возможности экспорта данных",
    "bi.feature7": "Пользовательские расчеты и метрики",
    "bi.feature8": "Встроенная интеграция аналитики",
    "bi.feature.unified": "Все ключевые показатели бизнеса в одном окне",
    "bi.feature.realtime": "Актуальные данные в реальном времени",
    "bi.feature.drilldown": "Понимание причин цифр — до деталей по клику",
    "bi.feature.automation": "Автоматические отчеты без ручной работы",
    "bi.feature.security": "Доступ к данным по ролям и безопасности",
    "bi.feature.integration": "Интеграция с CRM и другими системами",
    "bi.cta.title": "Готовы визуализировать данные?",
    "bi.cta.description": "Давайте создадим дашборды, которые превратят данные в конкурентное преимущество.",

    // Custom Solutions Page
    "custom.hero.label": "Решения",
    "custom.hero.title": "Кастомные IT-решения для бизнеса, когда коробка не работает",
    "custom.hero.description":
      "Мы проектируем и внедряем точечные программные решения под конкретные бизнес-задачи: интеграции, скоринг, автоматизация, нестандартная логика и данные.",
    "custom.overview.title": "Создано для вашего бизнеса",
    "custom.overview.description": "",
    "custom.overview.text": "",
    "custom.principle.title": "Наш принцип:",
    "custom.principle1": "Не переписываем платформы с нуля.",
    "custom.principle2": "Не продаём лишнюю разработку.",
    "custom.principle3": "Создаём ровно то, что нужно бизнесу — и только если без этого нельзя.",
    "custom.quote": "«Сначала — анализ. Потом — интеграция. И только потом — разработка.»",
    "custom.approach.intro": "Мы начинаем с ответа на вопрос:",
    "custom.approach.question": "можно ли решить задачу без кастомного кода?",
    "custom.approach.yes": "Если да — мы скажем об этом.",
    "custom.approach.no": "Если нет — разработаем оптимальное решение под ваши ограничения и цели.",
    "custom.partnership.title": "Партнерский подход",
    "custom.partnership.description": "Мы работаем вместе с вашей командой, а не просто для вас",
    "custom.when.title": "Когда мы нужны",
    "custom.when.reason1": "Стандартное ПО не покрывает ключевые процессы",
    "custom.when.reason2": "Требуется сложная интеграция нескольких систем",
    "custom.when.reason3": "Нужна уникальная логика принятия решений (скоринг, правила, ML)",
    "custom.when.reason4": "Есть данные, но нет инструментов, чтобы извлечь из них ценность",
    "custom.when.reason5": "Коробочное решение дорого, избыточно или плохо масштабируется",
    "custom.when.reason6": "Расширения платформ — Индивидуальные дополнения для существующих программных платформ",
    "custom.why.title": "Почему индивидуальная разработка?",
    "custom.why.reason1.title": "Идеальное соответствие",
    "custom.why.reason1.description": "ПО, спроектированное вокруг ваших процессов, а не наоборот",
    "custom.why.reason2.title": "Конкурентное преимущество",
    "custom.why.reason2.description": "Собственные инструменты, которые конкуренты не могут повторить",
    "custom.do.title": "Что мы делаем",
    "custom.do.description": "Точечные решения для конкретных бизнес-задач.",
    "custom.do.local.title": "Локальные кастомные решения",
    "custom.do.local.description":
      "Разрабатываем узкие, целевые модули под конкретную задачу бизнеса — без лишнего функционала и технического долга.",
    "custom.do.integration.title": "Сложные интеграции",
    "custom.do.integration.description":
      "Соединяем CRM, ERP, BI, платёжные системы, внешние API и legacy-системы в единый рабочий контур.",
    "custom.do.scoring.title": "Скоринговые и аналитические модели",
    "custom.do.scoring.description":
      "Создаём модели оценки рисков, клиентов, операций и процессов — от бизнес-правил до ML-алгоритмов.",
    "custom.do.automation.title": "Автоматизация нестандартных процессов",
    "custom.do.automation.description":
      'Оцифровываем то, что нельзя автоматизировать "из коробки": уникальные сценарии, исключения, сложные цепочки решений.',
    "custom.do.extensions.title": "Расширение существующих систем",
    "custom.do.extensions.description": "Достраиваем и усиливаем текущие IT-решения без полной замены платформы.",
    "custom.do.web.title": "Web решения",
    "custom.do.web.description": "Личный кабинет, приложения и другие решения для клиентского сервиса.",
    "custom.do.cta.text":
      "Опишите ваш кейс — мы скажем, где можно обойтись интеграцией, а где без кастома не обойтись.",
    "custom.do.cta.button": "Связаться с нами",
    "custom.types.title": "Что мы создаем",
    "custom.types.description": "Индивидуальные решения для разнообразных бизнес-потребностей.",
    "custom.type.apps.title": "Индивидуальные приложения",
    "custom.type.apps.description": "Веб и мобильные приложения, созданные по вашим точным спецификациям.",
    "custom.type.integration.title": "Системная интеграция",
    "custom.type.integration.description": "Объединение разрозненных систем в единую технологическую экосистему.",
    "custom.type.legacy.title": "Модернизация legacy",
    "custom.type.legacy.description":
      "Обновление устаревших систем современными технологиями с сохранением бизнес-логики.",
    "custom.type.extensions.title": "Расширения платформ",
    "custom.type.extensions.description": "Индивидуальные дополнения для существующих программных платформ.",
    "custom.process.title": "Как мы работаем",
    "custom.process.description": "Проверенный подход к созданию успешных индивидуальных решений.",
    "custom.process.step1.title": "Анализ бизнес-логики",
    "custom.process.step1.description": "Анализ бизнес-логики, рисков и регуляторных требований.",
    "custom.process.step2.title": "Проектирование архитектуры",
    "custom.process.step2.description": "Проектирование архитектуры решения.",
    "custom.process.step3.title": "Интеграция с экосистемой",
    "custom.process.step3.description": "Интеграция с текущей финтех-экосистемой.",
    "custom.process.step4.title": "Разработка модулей",
    "custom.process.step4.description": "Разработка кастомных модулей.",
    "custom.process.step5.title": "Тестирование и поддержка",
    "custom.process.step5.description": "Тестирование, передача и сопровождение.",
    "custom.approach.title": "Наш подход",
    "custom.approach1": "Совместные воркшопы по исследованию",
    "custom.approach2": "Методология дизайна, ориентированная на пользователя",
    "custom.approach3": "Гибкая итеративная разработка",
    "custom.approach4": "Комплексное тестирование и QA",
    "custom.approach5": "Стратегии поэтапного запуска",
    "custom.approach6": "Передача знаний и документация",
    "custom.support.title": "Постоянная поддержка",
    "custom.profitable.title": "Почему это выгодно бизнесу",
    "custom.profitable.cost.title": "Меньше затрат",
    "custom.profitable.cost.description": "Дешевле, чем разработка платформы с нуля",
    "custom.profitable.speed.title": "Быстрее внедрение",
    "custom.profitable.speed.description": "Фокус на одной задаче — быстрый результат",
    "custom.profitable.logic.title": "Точная логика",
    "custom.profitable.logic.description": 'Под ваш бизнес, а не "как задумал вендор"',
    "custom.profitable.control.title": "Полный контроль",
    "custom.profitable.control.description": "Контроль над данными и процессами",
    "custom.profitable.scale.title": "Масштабируемость",
    "custom.profitable.scale.description": "Масштабирование без переделок",
    "custom.profitable.vendor.title": "Независимость от вендоров",
    "custom.profitable.vendor.description": "Вы не привязаны к roadmap'у, лицензиям и ограничениям поставщика",
    "custom.support1": "Выделенная поддержка и обслуживание",
    "custom.support2": "Мониторинг и оптимизация производительности",
    "custom.support3": "Улучшения и обновления функций",
    "custom.support4": "Планирование и реализация масштабирования",
    "custom.support5": "Патчи безопасности и обновления соответствия",
    "custom.support6": "Регулярные обзоры и сессии по roadmap",
    "custom.cta.title": "Есть уникальная задача?",
    "custom.cta.description": "Давайте изучим, как индивидуальное решение может дать вам преимущество.",

    // Contact Form Titles per page
    "form.smm.title": "Готовы развивать присутствие в соцсетях?",
    "form.smm.description": "Давайте создадим стратегию, которая свяжет вас с аудиторией и принесет результаты.",
    "form.research.title": "Нужны инсайты для вашей стратегии?",
    "form.research.description": "Давайте обсудим, как исследование рынка может помочь вашим бизнес-решениям.",
    "form.crm.title": "Готовы трансформировать управление бизнесом?",
    "form.crm.description": "Давайте обсудим, как правильно внедренная CRM может продвинуть ваш бизнес.",
    "form.automation.title": "Готовы автоматизировать процессы?",
    "form.automation.description": "Давайте найдем возможность для оптимизации операций и повышения продуктивности.",
    "form.bi.title": "Готовы внедрить BI-аналитику?",
    "form.bi.description": "Давайте создадим дашборды, которые превратят данные в конкурентное преимущество!",
  },
  uz: {
    // Common
    "common.start": "Boshlash",
    "common.submitRequest": "So‘rov yuborish",

    // Header
    "nav.marketing": "Marketing",
    "nav.solutions": "Yechimlar",
    "nav.contact": "Aloqa",
    "nav.callback": "Qoʻngʻiroq buyurtma qilish",
    "nav.smm": "SMM",
    "nav.smm.desc": "Ijtimoiy tarmoqlarda marketing",
    "nav.research": "Bozor tadqiqoti",
    "nav.research.desc": "Ma'lumotlarga asoslangan tahlil",
    "nav.crm": "CRM joriy etish",
    "nav.crm.desc": "Mijozlarni boshqarish tizimlari",
    "nav.automation": "Jarayonlarni avtomatlashtirish",
    "nav.automation.desc": "Ish jarayonlarini optimallashtirish",
    "nav.bi": "BI Dashboardlar",
    "nav.bi.desc": "Biznes-tahlil",
    "nav.custom": "Individual yechimlar",
    "nav.custom.desc": "Maxsus ishlab chiqish",

    // Callback Modal
    "callback.title": "Qoʻngʻiroq buyurtma qilish",
    "callback.description": "Maʻlumotlaringizni qoldiring va biz 15 daqiqa ichida qayta qoʻngʻiroq qilamiz",
    "callback.name": "Ism",
    "callback.namePlaceholder": "Ismingiz",
    "callback.phone": "Telefon",
    "callback.sending": "Yuborilmoqda...",
    "callback.success": "Soʻrov yuborildi!",
    "callback.successDesc": "Biz sizga 15 daqiqa ichida qayta qoʻngʻiroq qilamiz",
    "callback.error": "Xato",
    "callback.fillFields": "Iltimos, barcha maydonlarni toʻgʻri toʻldiring",

    // Hero
    "hero.badge": "Raqamli biznes hamkor",
    "hero.title1": "Binar+",
    "hero.title2": "biznesni raqamli transformatsiya qilishda ishonchli hamkor",
    "hero.description":
      "Biz biznesga oʻlchanadigan oʻsishni taʻminlovchi strategik marketing va texnologik yechimlarni joriy etishga yordam beramiz.",
    "hero.feature1": "CRM va ERP integratsiya",
    "hero.feature2": "Jarayonlarni avtomatlashtirish",
    "hero.feature3": "BI tahlil",
    "hero.cta": "Boshlash",
    "hero.ctaSecondary": "Yechimlarni koʻrish",

    // Sections
    "marketing.label": "Marketing",
    "marketing.title": "Strategik marketing xizmatlari",
    "marketing.description":
      "Bizning keng qamrovli marketing yechimlarimiz bilan maqsadli auditoriyangizga yeting va brendingizni taniting.",

    // SMM Homepage Section
    "smm.label": "Marketing",
    "smm.title": "SMM Marketing",
    "smm.description":
      "Biz biznesga ijtimoiy tarmoqlarda mavjudligini rivojlantirishga yordam beramiz. Jamoamiz qiziqarli kontent yaratadi, reklama kampaniyalarini olib boradi va Instagram, Facebook, TikTok va boshqa platformalarda faol hamjamiyatlar quradi.",
    "smm.home.feature1": "Kontent strategiyasini ishlab chiqish",
    "smm.home.feature2": "Maqsadli reklama kampaniyalari",
    "smm.home.feature3": "Hamjamiyatni boshqarish",
    "smm.home.feature4": "Tahlil va hisobotlar",
    "smm.cta": "Batafsil",

    // Market Research Homepage Section
    "research.label": "Marketing",
    "research.title": "Bozor tadqiqoti",
    "research.description":
      "Biz auditoriyangiz, raqobatchilaringiz va bozor tendentsiyalarini tushunishga yordam berish uchun keng qamrovli bozor tadqiqotlarini oʻtkazamiz. Bizning tahliliy maʻlumotlarimiz yanada samarali biznes qarorlarini qabul qilishga imkon beradi.",
    "research.home.feature1": "Isteʻmolchilar xatti-harakatlarini tahlil qilish",
    "research.home.feature2": "Raqobatchilar razvedkasi",
    "research.home.feature3": "Bozor tendentsiyalarini prognozlash",
    "research.home.feature4": "Maʻlumotlarga asoslangan tavsiyalar",
    "research.cta": "Batafsil",

    // Budget Request Section
    "budget.promo.banner": "SMM Audit BEPUL 01.03.2026 gacha!",
    "budget.label": "Marketing yechimlari",
    "budget.title": "SMM Audit va strategiyasini buyurtma qiling",
    "budget.description":
      "Shaxsiy marketing byudjeti baholashini oling va bizning integratsiyalashgan texnologik stekimiz biznesingizni qanday oʻzgartirishini bilib oling.",
    "budget.feature1": "CRM, ERP va BI tizimlari bilan tʻliq integratsiya",
    "budget.feature2": "Avtomatlashtirilgan marketing jarayonlari",
    "budget.feature3": "Real vaqtda tahlil va hisobot",
    "budget.form.title": "Baholash olish",
    "budget.form.name": "Ism",
    "budget.form.namePlaceholder": "Ismingiz",
    "budget.form.phone": "Telefon",
    "budget.form.email": "Email",
    "budget.form.calculate": "Buyurtma qilish",
    "budget.form.sending": "Yuborilmoqda...",
    "budget.form.success": "Soʻrov yuborildi!",
    "budget.form.successDescription": "Mutaxassisimiz 24 soat ichida siz bilan bogʻlanadi.",
    "budget.form.error": "Xato",
    "budget.form.errorDescription": "Iltimos, majburiy maydonlarni toʻldiring.",

    // Systems Implementation Section
    "systems.label": "Yechimlar",
    "systems.title": "CRM, BPM, ERP joriy etish",
    "systems.description":
      "Biz korxona boshqaruv tizimlarini joriy etamiz va sozlaymiz, ular biznes jarayonlaringizni optimallashtiradi, samaradorlikni oshiradi va kompaniya faoliyatini toʻliq nazorat qilish imkonini beradi.",
    "systems.home.feature1": "Biznesingiz uchun tizimni tanlash va joriy etish",
    "systems.home.feature2": "Tizimni biznes vazifalaringizga moslashtirish",
    "systems.home.feature3": "Zarur tizimlar bilan integratsiya",
    "systems.home.feature4": "Xodimlarni tizimda ishlashga oʻqitish",
    "systems.cta": "Batafsil",

    // Automation Homepage Section
    "automation.label": "Yechimlar",
    "automation.title": "Biznes jarayonlarini avtomatlashtirish",
    "automation.description":
      "Biz biznesingizni oʻzgartirish uchun jarayonlarni robotlashtirish (RPA), AI agentlari, mashinali oʻrganish va aqlli ish jarayonlaridan foydalangan holda ilgʻor avtomatlashtirish yechimlarini joriy etamiz.",
    "automation.home.feature1": "Jarayonlarni robotlashtirish (RPA)",
    "automation.home.feature2": "AI agentlari va virtual yordamchilar",
    "automation.home.feature3": "Hujjatlarni intellektual qayta ishlash",
    "automation.home.feature4": "No-code/low-code avtomatlashtirish platformalari",
    "automation.cta": "Batafsil",

    // BI Homepage Section
    "bi.label": "Yechimlar",
    "bi.title": "Biznes-tahlil va BI-dashboardlar",
    "bi.description":
      "Biz sizning maʻlumotlaringizni strategik qarorlar qabul qilish uchun tushunarli tushunchalarga aylantiradigan interaktiv dashboardlar va tahliliy yechimlarni yaratamiz.",
    "bi.home.feature1": "Interaktiv Power BI dashboardlari",
    "bi.home.feature2": "Real vaqtda maʻlumotlarni vizualizatsiya qilish",
    "bi.home.feature3": "KPI monitoring va hisobot",
    "bi.home.feature4": "Turli manbalardan maʻlumotlarni integratsiya qilish",
    "bi.cta": "Batafsil",

    // Custom Solutions Homepage Section
    "custom.label": "Yechimlar",
    "custom.home.title": "Individual yechimlar",
    "custom.home.description":
      "Biz biznesingizning noyob ehtiyojlariga moslashtirilgan maxsus dasturiy taʻminot ishlab chiqamiz — veb-saytlar va mobil ilovalardan tortib qaror qabul qilish tizimlarigacha (skoring) va kompleks korporativ yechimlar.",
    "custom.home.feature1": "Korporativ saytlar va veb-ilovalar",
    "custom.home.feature2": "iOS va Android uchun mobil ilovalar",
    "custom.home.feature3": "Skoring va qaror qabul qilish tizimlari",
    "custom.home.feature4": "Integratsiya va maxsus API ishlab chiqish",
    "custom.cta": "Batafsil",

    // About Company Section
    "about.label": "Biz haqimizda",
    "about.title": "Nima uchun Binar+ ni tanlashadi",
    "about.description":
      "Biz Yevropa Ittifoqi va MDH bozorlaridagi bizneslar uchun yuqori yukli tizimlarni yaratish boʻyicha keng tajribaga ega fintech guruhining bir qismimiz.",
    "about.advantage1": "Yillik tajriba",
    "about.advantage2": "YeI va MDH bozorlarida tajriba",
    "about.advantage3": "Fintech guruhining aʻzosi",
    "about.advantage4": "Yuqori yukli loyihalar",

    // Principles Section
    "principles.label": "Bizning tamoyillarimiz",
    "principles.title": "Biz qanday ishlaymiz",
    "principles.principle1.title": "Har doim oxirigacha olib boramiz",
    "principles.principle1.description":
      "Biz har bir loyihani yakuniga yetkazamiz. Qiyinchiliklar qanday boʻlmasin, biz yechim topamiz va natijaga erishamiz.",
    "principles.principle2.title": "Natija uchun javobgarmiz",
    "principles.principle2.description":
      "Biz oʻz ishimiz uchun javobgarmiz va natijalar uchun masʻulmiz. Sizning muvaffaqiyatingiz bizning muvaffaqiyat mezonimiz.",
    "principles.principle3.title": "Biznesni tushunamiz",
    "principles.principle3.description":
      "Biz mijozning biznes xususiyatlarini hisobga olgan holda biznes tizimlarini qurishga yordam beramiz, qonunchilik talablari va xavfsizlik standartlariga albatta eʻtibor qaratamiz.",

    // Testimonials Section
    "testimonials.label": "Sharhlar",
    "testimonials.title": "Mijozlarimiz nima deyishadi",
    "testimonials.description":
      "Biz YeI, MDH va Markaziy Osiyo bozorlaridagi yetakchi kompaniyalar bilan ishlashdan faxrlanamiz.",
    "testimonials.country.belarus": "Belarus",
    "testimonials.country.russia": "Rossiya",
    "testimonials.country.kazakhstan": "Qozog'iston",
    "testimonials.review1":
      "Binar+ bizning sotish boʻlimimiz samaradorligini 40% ga oshirgan kompleks CRM tizimini joriy etdi. Ularning fintech boʻyicha tajribasi tengsiz.",
    "testimonials.review2":
      "Binar+ tomonidan taqdim etilgan avtomatlashtirish yechimlari qoʻlda qayta ishlash vaqtini 60% ga qisqartirishga yordam berdi. Yuqori malakali jamoa.",
    "testimonials.review3":
      "Bizga lizing portfelimizni real vaqtda tahlil qilish imkonini beradigan ajoyib BI dashboardlar. Bu qarorlar qabul qilishimizni tubdan oʻzgartirdi.",
    "testimonials.review4":
      "Binar+ bilan raqamli transformatsiya ustida ishlash muammosiz oʻtdi. Ular fintech regulyatsiyasi va xavfsizlik talablarini tushunishadi.",
    "testimonials.review5":
      "Binar+ tomonidan ishlab chiqilgan skoring tizimi bizning kredit qarorlarimiz aniqligini sezilarli darajada oshirdi. Ajoyib texnik tajriba.",
    "testimonials.review6":
      "Ularning jarayonlarni avtomatlashtirish yechimlari xodimlar sonini mutanosib ravishda oshirmasdan operatsiyalarimizni kengaytirishga yordam berdi.",
    "testimonials.review7":
      "Binar+ barcha biznes jarayonlarimizni birlashtirgan toʻliq ERP integratsiyasini amalga oshirdi. Professional va ishonchli hamkor.",
    "testimonials.review8":
      "Ular yaratgan marketing tahlili platformasi bizga mijozlarni jalb qilish hunisi haqida misli koʻrilmagan koʻrinishni beradi.",
    "testimonials.review9":
      "Konsultatsiyadan joriy etishgacha Binar+ texnologiyalar va biznes ehtiyojlarini chuqur tushunishini namoyish etdi.",
    "testimonials.author1": "Aleksandr Petrov",
    "testimonials.position1": "Texnik direktor",
    "testimonials.author2": "Mariya Ivanova",
    "testimonials.position2": "Operatsiyalar direktori",
    "testimonials.author3": "Dmitriy Kozlov",
    "testimonials.position3": "Bosh direktor",
    "testimonials.author4": "Yelena Smirnova",
    "testimonials.position4": "Digital rahbari",
    "testimonials.author5": "Sergey Volkov",
    "testimonials.position5": "IT direktor",
    "testimonials.author6": "Anna Kuznetsova",
    "testimonials.position6": "Operatsion direktor",
    "testimonials.author7": "Nursulton Axmetov",
    "testimonials.position7": "Boshqaruvchi direktor",
    "testimonials.author8": "Oida Nurbekova",
    "testimonials.position8": "Marketing direktori",
    "testimonials.author9": "Bekzat Yesenov",
    "testimonials.position9": "Bosh direktor",

    "solutions.label": "Yechimlar",
    "solutions.title": "Texnologik yechimlar",
    "solutions.description":
      "Operatsiyalarni optimallashtirish, samaradorlikni oshirish va biznes oʻsishini tezlashtirish uchun ilgʻor texnologiyalardan foydalaning.",

    // Services
    "service.smm.title": "Ijtimoiy tarmoqlarda marketing",
    "service.smm.description":
      "Barcha asosiy ijtimoiy platformalarda brend mavjudligini rivojlantiring va auditoriyani jalb qiling.",
    "service.research.title": "Bozor tadqiqoti",
    "service.research.description":
      "Biznes qarorlari uchun bozor, raqobatchilar va mijozlar haqida chuqur tushuncha oling.",
    "service.crm.title": "CRM joriy etish",
    "service.crm.description":
      "Mijozlar bilan munosabatlarni optimallashtiruvchi va sotish samaradorligini oshiruvchi CRM tizimlarini joriy eting.",
    "service.automation.title": "Jarayonlarni avtomatlashtirish",
    "service.automation.description":
      "Unumdorlikni oshirish va operatsion xarajatlarni kamaytirish uchun takrorlanuvchi vazifalarni avtomatlashtiring.",
    "service.bi.title": "BI Dashboardlar",
    "service.bi.description":
      "Individual biznes-tahlil dashboardlari yordamida ma'lumotlarni amaliy tushunchalarga aylantiring.",
    "service.custom.title": "Individual yechimlar",
    "service.custom.description":
      "Sizning noyob biznes muammolaringiz uchun maxsus ishlab chiqilgan dasturiy yechimlarni oling.",
    "service.learnMore": "Batafsil",

    // CTA Banner
    "cta.title": "Biznesingizni o'zgartirishga tayyormisiz?",
    "cta.description":
      "Keling, bizning yechimlarimiz maqsadlaringizga erishishga qanday yordam berishini muhokama qilaylik.",
    "cta.button": "Bog'lanish",

    // Footer
    "footer.about":
      "Raqamli transformatsiya orqali biznesga yordam beramiz. Oʻsishni taʻminlaydigan yechimlarni yaratish uchun strategik fikrlashni texnik tajriba bilan birlashtiramiz.",
    "footer.privacy": "Maxfiylik siyosati",
    "footer.terms": "Foydalanish shartlari",
    "footer.rights": "Barcha huquqlar himoyalangan.",
    "footer.address.line1": "Oʻzbekiston Respublikasi, 100170,",
    "footer.address.line2": "Toshkent shahri, Mirzo Ulugʻbek tumani,",
    "footer.address.line3": "Ziyolilar koʻchasi, 26-uy",
    "cookie.title": "Biz cookie fayllaridan foydalanamiz",
    "cookie.description":
      "Ushbu veb-sayt tajribangizni yaxshilash va sayt trafigini tahlil qilish uchun cookie fayllaridan foydalanadi. Saytdan foydalanishni davom ettirib, siz cookie fayllaridan foydalanishga rozilik bildirasiz.",
    "cookie.accept": "Qabul qilish",
    "cookie.decline": "Rad etish",
    "footer.form.title": "Biznesingizni oʻzgartirishga tayyormisiz?",
    "footer.form.description":
      "Keling, bizning yechimlarimiz maqsadlaringizga erishishga qanday yordam berishini muhokama qilaylik.",
    "footer.form.company": "Kompaniya nomi",
    "footer.form.companyPlaceholder": "Kompaniyangiz nomi",
    "footer.form.message": "Xabar",
    "footer.form.messagePlaceholder": "Loyihangiz haqida aytib bering...",
    "footer.form.submit": "So‘rov yuborish",

    // Contact Page
    "contact.title": "Keling, birga ishlaylik",
    "contact.subtitle":
      "Biznesingizni oʻzgartirishga tayyormisiz? Biz bilan bogʻlaning va maqsadlaringizga erishishga qanday yordam berishimiz mumkinligini muhokama qilaylik.",
    "contact.form.title": "Bizga xabar yuboring",
    "contact.form.description": "Quyidagi formani toʻldiring, bir ish kuni ichida javob beramiz.",
    "contact.form.name": "Ism",
    "contact.form.name.placeholder": "Ismingiz",
    "contact.form.email": "Email",
    "contact.form.email.placeholder": "sizning@email.com",
    "contact.form.company": "Kompaniya",
    "contact.form.company.placeholder": "Kompaniyangiz",
    "contact.form.service": "Qiziqtirgan xizmat",
    "contact.form.service.placeholder": "Xizmatni tanlang",
    "contact.form.message": "Xabar",
    "contact.form.message.placeholder": "Loyihangiz haqida gapirib bering yoki savol bering...",
    "contact.form.submit": "Yuborish",
    "contact.form.sending": "Yuborilmoqda...",
    "contact.form.success.title": "Xabar yuborildi!",
    "contact.form.success.description": "Tez orada siz bilan bogʻlanamiz.",
    "contact.info.title": "Aloqa maʻlumotlari",
    "contact.info.email": "Email",
    "contact.info.phone": "Telefon",
    "contact.info.office": "Ofis",
    "contact.social.title": "Ijtimoiy tarmoqlarda",
    "contact.map.title": "Bizni qanday topish mumkin",

    // Services dropdown
    "service.smm.label": "Ijtimoiy tarmoqlarda marketing",
    "service.research.label": "Bozor tadqiqoti",
    "service.crm.label": "CRM joriy etish",
    "service.automation.label": "Jarayonlarni avtomatlashtirish",
    "service.bi.label": "BI Dashboardlar",
    "service.custom.label": "Individual yechimlar",
    "service.other.label": "Boshqa",

    // SMM Page
    "smm.hero.label": "Marketing",
    "smm.hero.title": "Ijtimoiy tarmoqlarda marketing",
    "smm.hero.description":
      "Auditoriyangiz bilan mazmunli aloqalar yarating va barcha asosiy ijtimoiy platformalarda brend mavjudligini rivojlantiring.",
    "smm.overview.title": "Auditoriyangiz bilan u yerda bog'laning",
    "smm.overview.description":
      "O'zbekistonda SMM-marketing - bu internetda biznesni ilgari surishning eng samarali usullaridan biri. Biz layklar uchun emas, balki haqiqiy soʻrovlar, mijozlar va sotuvlar uchun ishlaymiz.",
    "smm.overview.benefit1": "Audit va maqsadli auditoriyani baholash",
    "smm.overview.benefit2": "Sizning biznesingiz uchun SMM-strategiya ishlab chiqish",
    "smm.overview.benefit3": "Akkauntlarni qadoqlash va rasmiylashtirish",
    "smm.overview.benefit4": "Sotuvchi va jalb qiluvchi kontent yaratish",
    "smm.overview.benefit5": "Maqsadli reklamani ishga tushirish va optimallashtirish",
    "smm.overview.benefit6": "Tahlil, sinov va natijalarni kengaytirish",
    "smm.overview.cta": "So‘rov yuborish",
    "smm.process.title": "Ish bosqichlari",
    "smm.process.description":
      "Muvaffaqiyatli ijtimoiy media strategiyalarini yaratish va amalga oshirishning tasdiqlangan yondashuvi.",
    "smm.process.step1.title": "Biznes tahlili",
    "smm.process.step1.description":
      "Nisha, maqsadli auditoriya, raqobatchilar va akkauntlarning hozirgi holatini oʻrganamiz.",
    "smm.process.step2.title": "Reklama strategiyasi",
    "smm.process.step2.description": "Maqsadlar, kontent formati, sotuv voronkasi va reklama modelini aniqlaymiz.",
    "smm.process.step3.title": "Akkaunt qadoqlash",
    "smm.process.step3.description":
      "Profilni ishonch uygʻotadigan va sizga yozishga undaydigan tarzda rasmiylashtiramiz.",
    "smm.process.step4.title": "Kontent va reklama",
    "smm.process.step4.description":
      "Muntazam kontent chiqaramiz + gipotezalar bilan maqsadli reklama ishga tushiramiz.",
    "smm.process.step5.title": "Tahlil va oʻsish",
    "smm.process.step5.description":
      "Koʻrsatkichlarni kuzatamiz, ishlaydigan bogʻlanishlarni kuchaytiramiz va natijani oshiramiz.",
    "smm.features.title": "Yakuniy natija",
    "smm.features.description":
      "Ijtimoiy tarmoqlar biznesingiz uchun ishlay boshlaydi, shunchaki mavjud boʻlib qolmaydi.",
    "smm.feature1": "Qamrov va brend tanilishining oʻsishi",
    "smm.feature2": "Ijtimoiy tarmoqlardan barqaror soʻrovlar oqimi",
    "smm.feature3": "Jonli va qiziquvchi auditoriya",
    "smm.feature4": "Mijoz (lid) narxining pasayishi",
    "smm.feature5": "Tushunarli va boshqariladigan mijozlarni jalb qilish tizimi",
    "smm.cta.title": "Ijtimoiy tarmoqlarda mavjudlikni rivojlantirishga tayyormisiz?",
    "smm.cta.description": "Keling, auditoriyangizni bogʻlaydigan va natija beradigan strategiya yarataylik.",
    "smm.platform.linkedin": "LinkedIn",
    "smm.platform.linkedin.desc": "B2B tarmoqlar va fikr etakchiligi",
    "smm.platform.instagram": "Instagram",
    "smm.platform.instagram.desc": "Vizual hikoya va tanilish",
    "smm.platform.facebook": "Facebook",
    "smm.platform.facebook.desc": "Jamiyat qurish va reklama",
    "smm.platform.twitter": "Twitter/X",
    "smm.platform.twitter.desc": "Real vaqt muloqoti",

    // Research Page
    "research.hero.label": "Marketing",
    "research.hero.title": "Bozor tadqiqoti",
    "research.hero.description":
      "Keng qamrovli bozor tahlili va mijozlarni tushunishga asoslangan ishonchli biznes qarorlarini qabul qiling.",
    "research.overview.title": "O'sishni rag'batlantiradigan tushunchalar",
    "research.overview.description":
      "Bozorni tushunish - biznes muvaffaqiyatining asosi. Tadqiqotlarimiz qatʻiy metodologiyani amaliy biznes tajribasi bilan birlashtiradi.",
    "research.overview.text":
      "Yangi bozorga kiryapsizmi, mahsulot ishga tushiryapsizmi yoki mijozlarni yaxshiroq tushunmoqchimisiz - ishonchli qadam tashlash uchun kerakli maʻlumot va tahlilni taqdim etamiz.",
    "research.overview.benefit1": "Birlamchi va ikkilamchi tadqiqotlar",
    "research.overview.benefit2": "Miqdoriy va sifat metodologiyalari",
    "research.overview.benefit3": "Sohaga oid ekspertiza",
    "research.overview.benefit4": "Amaliy strategik tavsiyalar",
    "research.decisions.title": "Tadqiqotlar qarorlarga qanday taʻsir qiladi",
    "research.decisions.step1.title": "Savolni aniqlash",
    "research.decisions.step1.desc": "Qanday qaror qabul qilish kerak?",
    "research.decisions.step2.title": "Ma'lumot toʻplash",
    "research.decisions.step2.desc": "Qaysi ma'lumotlar har bir variantni qoʻllab-quvvatlaydi?",
    "research.decisions.step3.title": "Tahlil va tavsiyalar",
    "research.decisions.step3.desc": "Ma'lumotlar nima deydi?",
    "research.decisions.step4.title": "Ishonch bilan harakat qilish",
    "research.decisions.step4.desc": "Kamaytirilgan xavf bilan oldinga.",
    "research.types.title": "Tadqiqot xizmatlari",
    "research.types.description": "Biznes ehtiyojlaringiz uchun keng qamrovli tadqiqot yechimlari.",
    "research.type.customer.title": "Mijozlar tadqiqoti",
    "research.type.customer.description":
      "Soʻrovlar, intervyular va xulq-atvor tahlili orqali mijozlarning ehtiyojlari, afzalliklari va muammolarini tushunish.",
    "research.type.competitor.title": "Raqobatchilar tahlili",
    "research.type.competitor.description":
      "Raqobat muhitini xaritalash, bozor boʻshliqlarini aniqlash va sohada etakchilar bilan taqqoslash.",
    "research.type.sizing.title": "Bozor hajmini baholash",
    "research.type.sizing.description":
      "Bozor imkoniyatlarini miqdoriy baholash, potentsialini baholash va oʻsish segmentlarini aniqlash.",
    "research.type.strategy.title": "Strategiyalar ishlab chiqish",
    "research.type.strategy.description":
      "Bozor maʻlumotlari asosida mahsulot, marketing, xodimlar, moliya va investitsiyalar boʻyicha kompleks strategiyalarni ishlab chiqish.",
    "research.process.title": "Bizning tadqiqot jarayonimiz",
    "research.process.description": "Amaliy bozor tushunchalarini ochish uchun tizimli yondashuv.",
    "research.process.step1.title": "Maqsadlarni aniqlash",
    "research.process.step1.description":
      "Asosiy savollar va tadqiqot maqsadlarini aniqlash uchun siz bilan ishlaymiz.",
    "research.process.step2.title": "Maʻlumot toʻplash",
    "research.process.step2.description": "Tasdiqlangan usullar orqali sifat va miqdoriy maʻlumotlarni toʻplash.",
    "research.process.step3.title": "Tahlil",
    "research.process.step3.description": "Tahlilchilar mazmunli naqshlar va amaliy tushunchalarni ajratib oladilar.",
    "research.process.step4.title": "Tavsiyalar",
    "research.process.step4.description": "Biznes qarorlarini qabul qilish uchun aniq strategik tavsiyalar.",
    "research.benefits.title": "Bozor tadqiqotining afzalliklari",
    "research.benefits.description": "Raqobat ustunligini qoʻlga kiritish uchun bozorni tushunishga sarmoya kiriting.",
    "research.benefit1": "Strategik qarorlarni qabul qilishda xavfni kamaytirish",
    "research.benefit2": "Yangi bozor imkoniyatlarini aniqlash",
    "research.benefit3": "Mijozlar ehtiyojlari va kutishlarini tushunish",
    "research.benefit4": "Raqobatchilar bilan taqqoslash",
    "research.benefit5": "Gʻoyalar, mahsulotlar va xizmatlarni ishga tushirishdan oldin tekshirish",
    "research.benefit6": "Narx strategiyalarini optimallashtirish",
    "research.benefit7": "Mijozlar qoniqishi va sadoqatini oshirish",
    "research.benefit8": "Investitsiya va oʻsish rejalashtirishni qoʻllab-quvvatlash",
    "research.cta.title": "Strategiyangiz uchun tushunchalar kerakmi?",
    "research.cta.description":
      "Keling, bozor tadqiqoti biznes qarorlaringizga qanday yordam berishini muhokama qilaylik.",

    // CRM Page
    "crm.hero.label": "Yechimlar",
    "crm.hero.title": "CRM joriy etish",
    "crm.hero.description":
      "Biznesingizga moslashtirilgan CRM tizimi bilan mijozlar bilan munosabatlarni oʻzgartiring.",
    "crm.overview.title": "Mijozlar bilan mustahkam munosabatlarni qurishning",
    "crm.overview.description":
      "Toʻgʻri joriy etilgan CRM - muvaffaqiyatli mijozlarni boshqarishning asosi. CRM yechimlarini tanlash, sozlash va joylashtirishda yordam beramiz.",
    "crm.benefit1": "Mijozlar bilan munosabatlarning 360 graduslik ko'rinishi",
    "crm.benefit2": "Savdo jamoasining yuqori samaradorligi",
    "crm.benefit3": "Bashoratlarning yaxshilangan aniqligi",
    "crm.benefit4": "Yaxshilangan mijozlarga xizmat",
    "crm.benefit5": "Optimallashtirilgan kommunikatsiya",
    "crm.benefit6": "Maʻlumotlarga asoslangan qarorlar",
    "crm.why.title": "Nima uchun professional joriy etish muhim",
    "crm.why.text1":
      "Koʻplab CRM joriy etishlari texnologiya tufayli emas, balki yomon rejalashtirish yoki yetarli moslashmaslik tufayli muvaffaqiyatsiz boʻladi.",
    "crm.why.text2":
      "Bizning yondashuvimiz CRM-ni jamoangiz haqiqiy ishlash usuliga moslashtiradi, toza maʻlumotlar migratsiyasi va oʻrgatish bilan.",
    "crm.why.result": "Natija: jamoangiz haqiqatan ham ishlatadigan va haqiqiy qiymat beradigan CRM.",
    "crm.services.title": "CRM xizmatlarimiz",
    "crm.services.description": "Biznesingiz uchun keng qamrovli CRM joriy etish yordami.",
    "crm.service.selection.title": "Tizim tanlash",
    "crm.service.selection.description":
      "Soha, hajm va talablaringiz asosida toʻgʻri CRM platformasini tanlashda yordam beramiz.",
    "crm.service.config.title": "Individual sozlash",
    "crm.service.config.description": "Maydonlar, ish jarayonlari, avtomatlashtirish va integratsiyalarni sozlash.",
    "crm.service.integration.title": "Integratsiya",
    "crm.service.integration.description":
      "CRM-ni email, marketing vositalari, buxgalteriya va boshqa tizimlar bilan ulash.",
    "crm.service.support.title": "Doimiy qoʻllab-quvvatlash",
    "crm.service.support.description":
      "Biznes rivojlanishi bilan doimiy optimallashtirish, muammolarni hal qilish va yangilanishlar.",
    "crm.process.title": "Joriy etish jarayoni",
    "crm.process.description": "Muvaffaqiyatli CRM joylashtirishga tizimli yondashuv.",
    "crm.process.step1.title": "Baholash",
    "crm.process.step1.description": "Joriy jarayonlarni tahlil qilamiz va ideal CRM yechimini aniqlaymiz.",
    "crm.process.step2.title": "Sozlash",
    "crm.process.step2.description":
      "CRM-ni ish jarayonlari, maʻlumotlar tuzilmasi va biznes qoidalariga moslashtirish.",
    "crm.process.step3.title": "Migratsiya",
    "crm.process.step3.description": "Mavjud maʻlumotlarni xavfsiz koʻchirish, tasdiqlash va tozalash.",
    "crm.process.step4.title": "Oʻrgatish",
    "crm.process.step4.description": "CRM imkoniyatlarini maksimal darajada ishlatish uchun keng qamrovli oʻrgatish.",
    "crm.results.title": "Joriy etishdan keyingi natija",
    "crm.results.description":
      "Kompaniya oddiy CRM emas, balki natijalarni boshqarish uchun markazlashtirilgan vositaga ega boʻladi.",
    "crm.result1": "Yagona mijozlar bazasi",
    "crm.result2": "Boshqariladigan sotish voronkasi",
    "crm.result3": "Shaffof tahlil va KPI",
    "crm.result4": "Sinxronlashtirilgan jamoa ishi",
    "crm.result5": "Boshqa tizimlar bilan integratsiya",
    "crm.result6": "Muntazam vazifalarni avtomatlashtirish",
    "crm.result7": "Mijozlar sodiqligini oshirish",
    "crm.result8": "Daromad oʻsishi",
    "crm.cta.title": "Mijozlarni boshqarishni oʻzgartirishga tayyormisiz?",
    "crm.cta.description":
      "Keling, toʻgʻri joriy etilgan CRM biznesingizni qanday oldinga surishini muhokama qilaylik.",
    "crm.cta.button": "So‘rov yuborish",

    // Automation Page
    "automation.hero.label": "Yechimlar",
    "automation.hero.title": "Jarayonlarni avtomatlashtirish",
    "automation.hero.description":
      "Aqlli avtomatlashtirish bilan qoʻlda ishlashni yoʻq qiling, xatolarni kamaytiring va biznes jarayonlarini tezlashtiring.",
    "automation.overview.title": "Aqlliroq ishlang, ko'proq emas",
    "automation.overview.description":
      "Har bir biznasda takrorlanuvchi vazifalar bor. Biz bu jarayonlarni topamiz va avtomatlashtiramiz, strategik ish uchun vaqt boʻshatamiz.",
    "automation.overview.cta": "So‘rov yuborish",
    "automation.benefit1": "Qoʻlda vazifalarni 80% gacha kamaytirish",
    "automation.benefit2": "Maʻlumotlarni kiritishda inson xatolarini yoʻq qilish",
    "automation.benefit3": "Qimmatli vazifalar uchun vaqt boʻshatish",
    "automation.benefit4": "Jarayonlarni bajarish vaqtini tezlashtirish",
    "automation.benefit5": "Izchillik va muvofiqlikni yaxshilash",
    "automation.benefit6": "Qoʻshimcha yollashsiz kengayish",
    "automation.roi.title": "Avtomatlashtirishdan iqtisodiy samara",
    "automation.roi.metric1.value": "80%",
    "automation.roi.metric1.label": "Qoʻlda ishlash vaqtini qisqartirish",
    "automation.roi.metric2.value": "99%",
    "automation.roi.metric2.label": "Avtomatik maʻlumotlar ishlash aniqligi",
    "automation.roi.metric3.value": "3-6 oy",
    "automation.roi.metric3.label": "Odatiy qaytarish muddati",
    "automation.roi.metric4.value": "35%+",
    "automation.roi.metric4.label": "Jarayonlarni optimallashtirish orqali foyda oʻsishi",
    "automation.roi.footnote": "Boshqa kompaniyalarga joriy etish natijalari asosida",
    "automation.types.title": "Avtomatlashtirish imkoniyatlari",
    "automation.types.description":
      "Operatsiyalarning barcha jihatlari uchun keng qamrovli avtomatlashtirish yechimlari.",
    "automation.type.workflow.title": "Ish jarayonlarini avtomatlashtirish",
    "automation.type.workflow.description":
      "Har qanday jarayonlar, har qanday boʻlimlar, har qanday raqamli ish joylarini avtomatlashtirish.",
    "automation.type.sales.title": "Sotuvlarni avtomatlashtirish",
    "automation.type.sales.description": "Liddan bitiruv kelishuvigacha toʻliq sotish siklini avtomatlashtirish.",
    "automation.type.marketing.title": "Marketingni avtomatlashtirish",
    "automation.type.marketing.description": "Kampaniyalar, segmentatsiya va mijoz aloqalarini avtomatlashtirish.",
    "automation.type.decisions.title": "Qaror qabul qilishni avtomatlashtirish",
    "automation.type.decisions.description": "Avtomatlashtirilgan biznes qarorlari uchun AI tizimlari.",
    "automation.type.documents.title": "Hujjat aylanishini avtomatlashtirish",
    "automation.type.documents.description": "Hujjatlarni yaratish, tasdiqlash va arxivlashni avtomatlashtirish.",
    "automation.type.analytics.title": "Maʻlumotlarni yigʻish va tahlil avtomatlashtirilishi",
    "automation.type.analytics.description": "Avtomatik maʻlumot yigʻish, qayta ishlash va tahlil.",
    "automation.type.payments.title": "Toʻlovlarni avtomatlashtirish",
    "automation.type.payments.description": "Toʻlovlarni qayta ishlash va taqqoslashni avtomatlashtirish.",
    "automation.type.reporting.title": "Hisobotlarni avtomatlashtirish",
    "automation.type.reporting.description": "Avtomatik hisobot yaratish va tarqatish.",
    "automation.process.title": "Avtomatlashtirish bosqichlari",
    "automation.process.description": "Barqaror avtomatlashtirish yechimlarini joriy etishga uslubiy yondashuv.",
    "automation.process.step1.title": "Tadqiqot",
    "automation.process.step1.description":
      "Avtomatlashtirish imkoniyatlarini aniqlash uchun joriy jarayonlarni xaritalaymiz.",
    "automation.process.step2.title": "Loyihalash",
    "automation.process.step2.description": "Biznes mantiqiga mos avtomatlashtirish ish jarayonlarini yaratish.",
    "automation.process.step3.title": "Joriy etish",
    "automation.process.step3.description": "Kuchli xato boshqarish bilan avtomatlashtirishlarni qurish va sinash.",
    "automation.process.step4.title": "Optimallashtirish",
    "automation.process.step4.description": "Maksimal samaradorlik uchun doimiy monitoring va takomillashtirish.",
    "automation.usecases.title": "Odatiy avtomatlashtirish stsenariylari",
    "automation.usecases.description": "Mijozlar uchun tez-tez avtomatlashtiradigan jarayonlar misollari.",
    "automation.usecase1": "Hisob-fakturalarni qayta ishlash va tasdiqlash",
    "automation.usecase2": "Mijozlarni onboarding ketma-ketligi",
    "automation.usecase3": "Xodimlarni onboarding avtomatlashtirilishi",
    "automation.usecase4": "Hisobotlarni yaratish va tarqatish",
    "automation.usecase5": "Tizimlar oʻrtasida maʻlumotlarni sinxronlashtirish",
    "automation.usecase6": "Bildirishnoma va ogohlantirish tizimlari",
    "automation.usecase7": "Buyurtmalarni qayta ishlash va bajarish",
    "automation.usecase8": "Shartnoma va hujjatlarni boshqarish",
    "automation.cta.title": "Jarayonlarni avtomatlashtirishga tayyormisiz?",
    "automation.cta.description":
      "Keling, operatsiyalarni optimallashtirish va unumdorlikni oshirish imkoniyatlarini topaylik.",

    // BI Dashboards Page
    "bi.hero.label": "Yechimlar",
    "bi.hero.title": "BI Dashboardlar",
    "bi.hero.description":
      "Individual biznes-tahlil dashboardlari yordamida maʻlumotlarni amaliy tushunchalarga aylantiring.",
    "bi.overview.title": "Biznesingizni aniq koʻring",
    "bi.overview.description":
      "Maʻlumotlaringiz hikoya qiladi. Biz bu hikoyani tushunarli va hammaga ochiq qiladigan dashboardlar yaratamiz.",
    "bi.benefit1": "Maʻlumotlarga asoslangan tez qarorlar",
    "bi.benefit2": "Trendlar va anomaliyalarni tez aniqlash",
    "bi.benefit3": "Tushunchalarni tashkilot boʻylab tarqatish",
    "bi.benefit4": "Qoʻlda hisobotni avtomatlashtirish bilan almashtirish",
    "bi.benefit5": "Bir nechta manbalardan maʻlumotlarni birlashtirish",
    "bi.benefit6": "Biznes maqsadlariga borishni kuzatish",
    "bi.transform.title": "Maʻlumotlar betartibligidan aniqlikka",
    "bi.transform.from1": "Tarqoq jadvallar",
    "bi.transform.to1": "Yagona dashboard",
    "bi.transform.from2": "Qoʻlda hisobotlar",
    "bi.transform.to2": "Real vaqtda yangilanishlar",
    "bi.transform.from3": "Statik raqamlar",
    "bi.transform.to3": "Interaktiv vizualizatsiyalar",
    "bi.transform.from4": "Soatlab tahlil",
    "bi.transform.to4": "Lahzali tushunchalar",
    "bi.types.title": "Dashboard yechimlari",
    "bi.types.description": "Biznesning har bir sohasi uchun maqsadli dashboardlar.",
    "bi.type.executive.title": "Rahbarlik dashboardlari",
    "bi.type.executive.description":
      "Chuqur tahlil uchun drill-down imkoniyati bilan yuqori darajadagi KPI koʻrinishlari.",
    "bi.type.sales.title": "Sotish tahlili",
    "bi.type.sales.description": "Voronka kuzatish, daromad bashorati va sotish samaradorligi metrikalari.",
    "bi.type.financial.title": "Moliyaviy hisobot",
    "bi.type.financial.description": "P&L vizualizatsiyasi, byudjet kuzatish va pul oqimi tahlili.",
    "bi.type.operational.title": "Operatsion metrikalar",
    "bi.type.operational.description": "Operatsiyalar, zaxiralar va xizmat koʻrsatishni real vaqtda monitoring.",
    "bi.type.endtoend.title": "Yaxlit tahlil",
    "bi.type.endtoend.description":
      "Marketing va sotish samaradorligi haqida toʻliq tasavvur uchun barcha kanallardan maʻlumotlarni birlashtirish.",
    "bi.type.monitoring.title": "Kompaniya monitoringi",
    "bi.type.monitoring.description":
      "Biznes salomatligi va jamoa samaradorligining asosiy koʻrsatkichlarini real vaqtda kuzatish.",
    "bi.process.title": "Dashboard ishlab chiqish jarayoni",
    "bi.process.description": "Qiymat beradigan dashboardlarni yaratishga hamkorlik yondashuvi.",
    "bi.process.step1.title": "Maʻlumotlar auditi",
    "bi.process.step1.description": "Maʻlumotlar manbalari, sifati va integratsiya talablarini baholaymiz.",
    "bi.process.step2.title": "Dizayn",
    "bi.process.step2.description": "KPI-laringizga mos maketlar va vizualizatsiyalar yaratish.",
    "bi.process.step3.title": "Ishlab chiqish",
    "bi.process.step3.description": "Real vaqtda maʻlumotlar ulanishi bilan interaktiv dashboardlar qurish.",
    "bi.process.step4.title": "Iteratsiya",
    "bi.process.step4.description": "Fikr-mulohazalar va rivojlanayotgan ehtiyojlar asosida takomillashtirish.",
    "bi.features.title": "Dashboard xususiyatlari",
    "bi.features.description": "Yaratilgan har bir dashboardda zamonaviy BI imkoniyatlari.",
    "bi.feature1": "Drill-down bilan interaktiv vizualizatsiyalar",
    "bi.feature2": "Real vaqtda maʻlumotlar yangilanishi",
    "bi.feature3": "Mobil qurilmalar uchun moslashuvchan dizayn",
    "bi.feature4": "Rollarga asoslangan kirish boshqaruvi",
    "bi.feature5": "Avtomatik hisobot rejalashtirish",
    "bi.feature6": "Maʻlumotlarni eksport qilish imkoniyatlari",
    "bi.feature7": "Maxsus hisob-kitoblar va metrikalar",
    "bi.feature8": "Oʻrnatilgan tahlil integratsiyasi",
    "bi.feature.unified": "Barcha asosiy biznes koʻrsatkichlari bitta oynada",
    "bi.feature.realtime": "Real vaqtda dolzarb maʻlumotlar",
    "bi.feature.drilldown": "Raqamlar ortidagi sabablarni tushunish — bosganda tafsilotlar",
    "bi.feature.automation": "Qoʻl ishi holda avtomatik hisobotlar",
    "bi.feature.security": "Rollarga asoslangan maʻlumotlarga kirish va xavfsizlik",
    "bi.feature.integration": "CRM va boshqa tizimlar bilan integratsiya",
    "bi.cta.title": "Maʻlumotlarni vizualizatsiya qilishga tayyormisiz?",
    "bi.cta.description": "Keling, maʻlumotlarni raqobat ustunligiga aylantiradigan dashboardlar yarataylik.",

    // Custom Solutions Page
    "custom.hero.label": "Yechimlar",
    "custom.hero.title": "Biznes uchun individual IT yechimlar — tayyor yechimlar ishlamasa",
    "custom.hero.description":
      "Biz aniq biznes vazifalari uchun maqsadli dasturiy yechimlarni loyihalaymiz va joriy etamiz: integratsiyalar, skoring, avtomatlashtirish, maxsus mantiq va maʻlumotlar.",
    "custom.overview.title": "Biznesingiz uchun yaratilgan",
    "custom.overview.description": "",
    "custom.overview.text": "",
    "custom.principle.title": "Bizning tamoyilimiz:",
    "custom.principle1": "Platformalarni noldan qayta yozmaymiz.",
    "custom.principle2": "Keraksiz ishlab chiqish sotmaymiz.",
    "custom.principle3": "Biznesga kerak boʻlgan narsani quramiz — va faqat zarur boʻlganda.",
    "custom.quote": '"Avval — tahlil. Keyin — integratsiya. Va faqat keyin — ishlab chiqish."',
    "custom.approach.intro": "Biz savolga javob berishdan boshlaymiz:",
    "custom.approach.question": "vazifani maxsus kodsiz hal qilish mumkinmi?",
    "custom.approach.yes": "Ha boʻlsa — aytamiz.",
    "custom.approach.no": "Yoʻq boʻlsa — cheklovlar va maqsadlaringiz uchun optimal yechim ishlab chiqamiz.",
    "custom.partnership.title": "Hamkorlik yondashuvi",
    "custom.partnership.description": "Jamoangiz bilan birga ishlaymiz, shunchaki siz uchun emas",
    "custom.when.title": "Qachon biz kerak bo'lamiz",
    "custom.when.reason1": "Standart dastur asosiy jarayonlarni qamrab olmaydi",
    "custom.when.reason2": "Bir nechta tizimlarning murakkab integratsiyasi kerak",
    "custom.when.reason3": "Noyob qaror qabul qilish mantigʻi kerak (skoring, qoidalar, ML)",
    "custom.when.reason4": "Maʻlumotlaringiz bor, lekin qiymat olish vositangiz yoʻq",
    "custom.when.reason5": "Tayyor yechim qimmat, ortiqcha yoki yomon miqyoslashadi",
    "custom.when.reason6": "Platforma kengaytmalari — mavjud dasturiy platformalar uchun maxsus qoʻshimchalar",
    "custom.why.title": "Nima uchun individual ishlab chiqish?",
    "custom.why.reason1.title": "Mukammal moslik",
    "custom.why.reason1.description": "Jarayonlaringiz atrofida loyihalangan dasturiy taʻminot, aksincha emas",
    "custom.why.reason2.title": "Raqobat ustunligi",
    "custom.why.reason2.description": "Raqobatchilar takrorlay olmaydigan shaxsiy vositalar",
    "custom.why.reason3.title": "Toʻliq nazorat",
    "custom.why.reason3.description": "Biznes oʻzgarganda rivojlaning va moslashing",
    "custom.do.title": "Biz nima qilamiz",
    "custom.do.description": "Aniq biznes vazifalari uchun maqsadli yechimlar.",
    "custom.do.local.title": "Mahalliy maxsus yechimlar",
    "custom.do.local.description":
      "Biznesning aniq vazifasi uchun tor, maqsadli modullar ishlab chiqamiz — ortiqcha funksionallik va texnik qarzsiz.",
    "custom.do.integration.title": "Murakkab integratsiyalar",
    "custom.do.integration.description":
      "CRM, ERP, BI, toʻlov tizimlari, tashqi API va eski tizimlarni yagona ish muhitiga bogʻlaymiz.",
    "custom.do.scoring.title": "Skoring va analitik modellar",
    "custom.do.scoring.description":
      "Xavf, mijoz, operatsiyalar va jarayonlarni baholash modellarini yaratamiz — biznes qoidalaridan ML algoritmlarigacha.",
    "custom.do.automation.title": "Nostandart jarayonlarni avtomatlashtirish",
    "custom.do.automation.description":
      '"Qutidan" avtomatlashtirib boʻlmaydigan narsalarni raqamlashtirish: noyob stsenariylar, istisnolar, murakkab qaror zanjirlari.',
    "custom.do.extensions.title": "Mavjud tizimlarni kengaytirish",
    "custom.do.extensions.description":
      "Joriy IT yechimlarni platformani toʻliq almashtirishsiz kuchaytiramiz va quramiz.",
    "custom.do.web.title": "Web yechimlar",
    "custom.do.web.description": "Shaxsiy kabinet, ilovalar va mijozlarga xizmat koʻrsatish uchun boshqa yechimlar.",
    "custom.do.cta.text":
      "Keysingizni tasvirlab bering — integratsiya yetarlimi yoki maxsus ishlab chiqish kerakmi, aytamiz.",
    "custom.do.cta.button": "Biz bilan bogʻlaning",
    "custom.types.title": "Biz nima yaratamiz",
    "custom.types.description": "Turli biznes ehtiyojlari uchun individual yechimlar.",
    "custom.type.apps.title": "Individual ilovalar",
    "custom.type.apps.description": "Aniq spetsifikatsiyalaringiz boʻyicha yaratilgan veb va mobil ilovalar.",
    "custom.type.integration.title": "Tizim integratsiyasi",
    "custom.type.integration.description": "Turli tizimlarni yagona texnologik ekotizimga birlashtirish.",
    "custom.type.legacy.title": "Legacy modernizatsiyasi",
    "custom.type.legacy.description":
      "Eskirgan tizimlarni biznes mantiqini saqlagan holda zamonaviy texnologiyalar bilan yangilash.",
    "custom.type.extensions.title": "Platforma kengaytmalari",
    "custom.type.extensions.description": "Mavjud dasturiy platformalar uchun individual qoʻshimchalar.",
    "custom.process.title": "Qanday ishlaymiz",
    "custom.process.description": "Muvaffaqiyatli individual yechimlarni yaratishga tasdiqlangan yondashuv.",
    "custom.process.step1.title": "Biznes mantiq tahlili",
    "custom.process.step1.description": "Biznes mantiq, xatarlar va tartibga solish talablarini tahlil qilish.",
    "custom.process.step2.title": "Arxitektura loyihalash",
    "custom.process.step2.description": "Yechim arxitekturasini loyihalash.",
    "custom.process.step3.title": "Ekotizim integratsiyasi",
    "custom.process.step3.description": "Joriy fintek ekotizimi bilan integratsiya.",
    "custom.process.step4.title": "Maxsus ishlab chiqish",
    "custom.process.step4.description": "Maxsus modullarni ishlab chiqish.",
    "custom.process.step5.title": "Test va qoʻllab-quvvatlash",
    "custom.process.step5.description": "Sinovdan oʻtkazish, topshirish va qoʻllab-quvvatlash.",
    "custom.approach.title": "Bizning yondashuvimiz",
    "custom.approach1": "Hamkorlik tadqiqot seminarlari",
    "custom.approach2": "Foydalanuvchiga yoʻnaltirilgan dizayn metodologiyasi",
    "custom.approach3": "Moslashuvchan iterativ ishlab chiqish",
    "custom.approach4": "Keng qamrovli test va sifat nazorati",
    "custom.approach5": "Bosqichma-bosqich ishga tushirish strategiyalari",
    "custom.approach6": "Bilim uzatish va hujjatlashtirish",
    "custom.support.title": "Doimiy qoʻllab-quvvatlash",
    "custom.profitable.title": "Nima uchun bu biznes uchun foydali",
    "custom.profitable.cost.title": "Kamroq xarajat",
    "custom.profitable.cost.description": "Platformani noldan yaratishdan arzonroq",
    "custom.profitable.speed.title": "Tezroq joriy etish",
    "custom.profitable.speed.description": "Bitta vazifaga eʻtibor — tez natija",
    "custom.profitable.logic.title": "Aniq mantiq",
    "custom.profitable.logic.description": 'Biznesingiz uchun, "vendor oʻylagandek" emas',
    "custom.profitable.control.title": "Toʻliq nazorat",
    "custom.profitable.control.description": "Maʻlumotlar va jarayonlar ustidan nazorat",
    "custom.profitable.scale.title": "Miqyoslilik",
    "custom.profitable.scale.description": "Qayta ishlashsiz kengaytirish",
    "custom.profitable.vendor.title": "Vendor mustaqilligi",
    "custom.profitable.vendor.description":
      "Siz yoʻl xaritasi, litsenziyalar va yetkazib beruvchi cheklovlariga bogʻliq emassiz",
    "custom.support1": "Maxsus qoʻllab-quvvatlash va xizmat koʻrsatish",
    "custom.support2": "Ishlash monitoring va optimallashtirish",
    "custom.support3": "Funksiya yaxshilanishlari va yangilanishlar",
    "custom.support4": "Kengaytirish rejalashtirish va amalga oshirish",
    "custom.support5": "Xavfsizlik yamoqlari va muvofiqlik yangilanishlari",
    "custom.support6": "Muntazam koʻrib chiqish va roadmap sessiyalari",
    "custom.cta.title": "Noyob muammongiz bormi?",
    "custom.cta.description": "Keling, individual yechim sizga qanday ustunlik berishini oʻrganaylik.",

    // Contact Form Titles per page
    "form.smm.title": "Ijtimoiy tarmoqlarda mavjudlikni rivojlantirishga tayyormisiz?",
    "form.smm.description": "Keling, auditoriyangizni bogʻlaydigan va natija beradigan strategiya yarataylik.",
    "form.research.title": "Strategiyangiz uchun tahlil kerakmi?",
    "form.research.description":
      "Keling, bozor tadqiqoti biznes qarorlaringizga qanday yordam berishini muhokama qilaylik.",
    "form.crm.title": "Biznesni boshqarishni oʻzgartirishga tayyormisiz?",
    "form.crm.description":
      "Keling, toʻgʻri joriy etilgan CRM biznesingizni qanday rivojlantirishini muhokama qilaylik.",
    "form.automation.title": "Jarayonlarni avtomatlashtirishga tayyormisiz?",
    "form.automation.description":
      "Keling, operatsiyalarni optimallashtirish va samaradorlikni oshirish imkoniyatini topaylik.",
    "form.bi.title": "BI-tahlilni joriy etishga tayyormisiz?",
    "form.bi.description": "Keling, maʻlumotlaringizni raqobat ustunligiga aylantiradigan dashboardlar yarataylik!",
  },
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<Language>("ru");

  const t = (key: string): string => {
    return translations[language][key] || translations.en[key] || key;
  };

  return <LanguageContext.Provider value={{ language, setLanguage, t }}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}
